/**
 * Action Execution Module
 * Contains HybridTargetResolver (multi-strategy element finder) and ActionExecutor (UI action execution)
 */

import { HybridTarget, HybridUIAction, ResolutionCandidate, ResolutionResult, UIAction, isSDKWidget, stripAngularStateClasses, humanLikeClick, querySelectorWithAngularFallback } from './types';
import { DOMIntrospector } from './dom-capture';
import { resolveSupportFileTarget, resolveSupportReveal, resolveSupportTarget, resultTransitionEvidenceFromSignature, resultTransitionSignature, supportChecksPass, supportChecksValid, supportFileTargetStillBound, supportPageMatches } from './support-step';
import { selectSupportFile, type SupportFileSelectionEvidence } from './support-file-selection';
import { SupportRecordExecution } from './support-record-execution';
import { findVisibleDropdownOption as _findVisibleDropdownOption } from './dropdown-helpers';
import { logger as defaultLogger, Logger } from './logger';
import {
  attemptSpaNavigation,
  createBrowserNavigationAdapter,
  resolveNavigationTarget,
  sameLocation,
  FULL_LOAD_GRACE_MS,
  type SpaNavigationResult,
} from './spa-navigation';

/**
 * Hybrid Target Resolver - Multi-strategy element finder
 * Enhanced with DOM Introspection Layer support
 * Tries multiple methods to locate an element on the page
 */
export class HybridTargetResolver {
  // Static reference to DOMIntrospector for bounding box lookups
  private static domIntrospector: DOMIntrospector | null = null;
  // Instance-scoped logger, updated by ActionExecutor when it is constructed.
  private static _logger: Logger = defaultLogger;
  static setLogger(l: Logger): void { HybridTargetResolver._logger = l; }
  /**
   * Extract parent context from a CSS selector.
   * This is a GENERIC approach that uses the selector's own structure to guide fallback matching.
   * 
   * For example, if selector is "button.MuiButtonBase-root > span.MuiTypography-root":
   * - expectedTag: 'span' (the leaf element we're looking for)
   * - parentSelector: 'button.MuiButtonBase-root' (the expected parent)
   * - parentTag: 'button' (simplified parent tag for quick checks)
   * 
   * This allows us to boost elements that match the expected DOM structure without
   * hardcoding specific words or class names.
   */
  private static extractSelectorContext(selector?: string): {
    expectedTag?: string;
    parentSelector?: string;
    parentTag?: string;
  } {
    if (!selector) return {};

    // Split by combinators: >, space, +, ~
    // We want the parent context (everything before the last combinator)
    const parts = selector.split(/\s*([>+~]|\s)\s*/);

    if (parts.length < 2) return {};

    // Last part is the target element, everything before is parent context
    const leafPart = parts[parts.length - 1];
    const parentParts = parts.slice(0, -2); // Exclude combinator and leaf

    // Extract tag from leaf (e.g., "span.class" -> "span")
    const leafTagMatch = leafPart.match(/^([a-zA-Z][a-zA-Z0-9-]*)/);
    const expectedTag = leafTagMatch ? leafTagMatch[1].toLowerCase() : undefined;

    // Build parent selector from parent parts
    const parentSelector = parentParts.length > 0 ? parentParts.join(' ') : undefined;

    // Extract simple parent tag (e.g., "button.class" -> "button")
    let parentTag: string | undefined;
    if (parentParts.length > 0) {
      const lastParent = parentParts[parentParts.length - 1];
      const parentTagMatch = lastParent.match(/^([a-zA-Z][a-zA-Z0-9-]*)/);
      parentTag = parentTagMatch ? parentTagMatch[1].toLowerCase() : undefined;
    }

    return { expectedTag, parentSelector, parentTag };
  }

  /**
   * Check if an element matches the expected parent context from selector.
   * Returns a score from 0 to 1 indicating how well it matches.
   * This is a GENERIC approach that uses the selector's structure.
   */
  private static matchesSelectorContext(
    element: HTMLElement,
    context: { expectedTag?: string; parentSelector?: string; parentTag?: string }
  ): number {
    if (!context.parentTag && !context.parentSelector) return 0.5; // No context, neutral

    let score = 0;
    let checks = 0;

    // Check if element is inside expected parent tag
    if (context.parentTag) {
      checks++;
      const parent = element.closest(context.parentTag);
      if (parent) {
        score += 1;
      }
    }

    // Check if element matches more specific parent selector
    if (context.parentSelector) {
      checks++;
      try {
        const parent = element.closest(context.parentSelector);
        if (parent) {
          score += 1;
        }
      } catch (e) {
        // Invalid selector, ignore
      }
    }

    return checks > 0 ? score / checks : 0.5;
  }

  /**
   * Set the DOM Introspector instance for bounding box resolution
   */
  static setDOMIntrospector(introspector: DOMIntrospector) {
    HybridTargetResolver.domIntrospector = introspector;
    HybridTargetResolver._logger.log('[HybridTargetResolver] DOM Introspector set for enhanced targeting');
  }

  /**
   * Safely serialize an object for logging, excluding DOM elements and circular references
   */
  private static safeStringify(obj: any, indent?: number): string {
    const seen = new WeakSet();
    return JSON.stringify(obj, (_key, value) => {
      // Skip DOM elements and circular references
      if (value && typeof value === 'object') {
        if (value instanceof HTMLElement) {
          const tagName = value.tagName || 'Unknown';
          const id = value.id ? `#${value.id}` : '';
          const className = value.className && typeof value.className === 'string'
            ? `.${value.className.split(' ').filter(Boolean).join('.')}`
            : '';
          return `[HTMLElement: ${tagName}${id}${className}]`;
        }
        if (value instanceof Element) {
          const tagName = value.tagName || 'Unknown';
          return `[Element: ${tagName}]`;
        }
        if (value instanceof Node) {
          return `[Node: ${value.nodeName}]`;
        }
        if (seen.has(value)) {
          return '[Circular]';
        }
        seen.add(value);
      }
      return value;
    }, indent);
  }

  /**
   * Resolve a target using multiple strategies
   * Now prioritizes DOM Introspection Layer data when available
   * Returns the first element found, or null if none found
   */
  static resolveTarget(target: HybridTarget): HTMLElement | null {
    if (!target) {
      HybridTargetResolver._logger.warn('[HybridTargetResolver] ❌ No target provided');
      return null;
    }

    if (HybridTargetResolver._logger.isDebug) HybridTargetResolver._logger.log('[HybridTargetResolver] Target details:', this.safeStringify(target, 2));

    // Use consensus-based resolution for all strategies (including DOM Index and Bounding Box)
    const consensusResult = this.resolveTargetWithConsensus(target);

    // Safely serialize consensus result, replacing DOM element with summary
    const elementSummary = consensusResult.element
      ? `[HTMLElement: ${consensusResult.element.tagName}${consensusResult.element.id ? `#${consensusResult.element.id}` : ''}${consensusResult.element.className && typeof consensusResult.element.className === 'string' ? `.${consensusResult.element.className.split(' ').filter(Boolean).join('.')}` : ''}]`
      : null;
    const safeResult = {
      ...consensusResult,
      element: elementSummary
    };
    if (HybridTargetResolver._logger.isDebug) HybridTargetResolver._logger.log(`[HybridTargetResolver] consensus results (${consensusResult.confidence.toFixed(1)}% confidence)`, this.safeStringify(safeResult, 2));
    if (consensusResult.element) {
      return consensusResult.element;
    }
    return null;
  }

  /**
   * Resolve target using parallel strategy execution with consensus voting
   * This is the new robust approach that runs all strategies and picks the best candidate
   */
  static resolveTargetWithConsensus(target: HybridTarget): ResolutionResult {
    const startTime = performance.now();

    // 1. Get all applicable strategies
    const strategies = this.getAvailableStrategies(target);

    // Edge case: No strategies available
    if (strategies.length === 0) {
      HybridTargetResolver._logger.warn('[HybridTargetResolver] ⚠️ No strategies available for target');
      return {
        element: null,
        confidence: 0,
        strategies: [],
        candidateCount: 0,
        ambiguous: false,
        duration: performance.now() - startTime
      };
    }

    HybridTargetResolver._logger.log(`[HybridTargetResolver] 🔄 Running ${strategies.length} strategies in parallel...`);

    // 2. Execute ALL strategies and collect candidates
    const candidateMap = new Map<HTMLElement, ResolutionCandidate>();

    for (const strategy of strategies) {
      const elements = this.executeStrategyMulti(strategy, target);

      for (const element of elements) {
        // Skip SDK widgets
        if (isSDKWidget(element)) continue;

        // Skip if element was removed from DOM
        if (!element.isConnected) continue;

        // Add to candidate map or update existing
        if (candidateMap.has(element)) {
          candidateMap.get(element)!.strategies.push(strategy);
        } else {
          candidateMap.set(element, {
            element,
            strategies: [strategy],
            validationScore: 0,
            interactable: false,
            confidence: 0
          });
        }
      }
    }

    HybridTargetResolver._logger.log(`[HybridTargetResolver] 📊 Found ${candidateMap.size} unique candidate(s)`);

    if (candidateMap.size === 0) {
      return {
        element: null,
        confidence: 0,
        strategies: [],
        candidateCount: 0,
        ambiguous: false,
        duration: performance.now() - startTime
      };
    }

    // 3. Score each candidate
    const scoredCandidates: ResolutionCandidate[] = [];

    for (const [element, candidate] of candidateMap) {
      // Re-check if element is still connected (might have been removed)
      if (!element.isConnected) {
        HybridTargetResolver._logger.warn(`[HybridTargetResolver]   ⚠️ Candidate element was removed from DOM, skipping`);
        continue;
      }

      candidate.validationScore = this.computeValidationScore(element, target);
      candidate.interactable = this.checkInteractability(element).ok;
      candidate.confidence = this.computeConfidence(candidate, strategies.length, target);
      scoredCandidates.push(candidate);

      if (HybridTargetResolver._logger.isDebug) {
        HybridTargetResolver._logger.log(`[HybridTargetResolver]   <${element.tagName.toLowerCase()}> "${element.textContent?.slice(0, 25)}..."`);
        HybridTargetResolver._logger.log(`[HybridTargetResolver]     Strategies: ${candidate.strategies.join(', ')} (${candidate.strategies.length}/${strategies.length})`);
        HybridTargetResolver._logger.log(`[HybridTargetResolver]     Confidence: ${candidate.confidence.toFixed(1)}%`);
      }
    }

    // Edge case: All candidates were removed from DOM
    if (scoredCandidates.length === 0) {
      return {
        element: null,
        confidence: 0,
        strategies: [],
        candidateCount: candidateMap.size,
        ambiguous: false,
        duration: performance.now() - startTime
      };
    }

    // 4. Sort by confidence
    scoredCandidates.sort((a, b) => b.confidence - a.confidence);

    const winner = scoredCandidates[0];
    const runnerUp = scoredCandidates.length > 1 ? scoredCandidates[1] : undefined;
    const duration = performance.now() - startTime;

    // Check for ambiguity (only if we have multiple candidates)
    const ambiguous = runnerUp !== undefined && (winner.confidence - runnerUp.confidence) < 10;
    if (ambiguous) {
      HybridTargetResolver._logger.warn(`[HybridTargetResolver] ⚠️ AMBIGUOUS: Top candidates have similar confidence`);
    }

    // Reject if confidence too low
    if (winner.confidence < 30) {
      HybridTargetResolver._logger.warn(`[HybridTargetResolver] ❌ Best candidate confidence too low: ${winner.confidence.toFixed(1)}%`);
      return {
        element: null,
        confidence: winner.confidence,
        strategies: winner.strategies,
        candidateCount: candidateMap.size,
        ambiguous,
        duration
      };
    }

    // Prefer interactable candidates
    if (!winner.interactable) {
      // First, try to find a clickable container for the hidden winner.
      // This handles hidden radio/checkbox inputs in Angular, MUI, Ant Design etc.
      // where the native input is hidden but the wrapper/label is clickable.
      const containerForWinner = this.getClickableContainer(winner.element);
      if (containerForWinner !== winner.element && HybridTargetResolver.checkInteractability(containerForWinner).ok) {
        HybridTargetResolver._logger.log(`[HybridTargetResolver] ✅ Using clickable container for hidden winner (${winner.confidence.toFixed(1)}%)`);
        return {
          element: containerForWinner,
          confidence: winner.confidence,
          strategies: winner.strategies,
          candidateCount: candidateMap.size,
          ambiguous,
          duration
        };
      }

      // No container found — fall back to first interactable candidate
      const interactableWinner = scoredCandidates.find(c => c.interactable);
      if (interactableWinner && interactableWinner.confidence > 30) {
        HybridTargetResolver._logger.log(`[HybridTargetResolver] ✅ Using interactable candidate (${interactableWinner.confidence.toFixed(1)}%)`);
        // Apply getClickableContainer for React Select/MUI compatibility
        const finalElement = this.getClickableContainer(interactableWinner.element);
        return {
          element: finalElement,
          confidence: interactableWinner.confidence,
          strategies: interactableWinner.strategies,
          candidateCount: candidateMap.size,
          ambiguous,
          duration
        };
      }
    }

    HybridTargetResolver._logger.log(`[HybridTargetResolver] ✅ Winner: ${winner.confidence.toFixed(1)}% confidence (${duration.toFixed(1)}ms)`);

    // CRITICAL: If the found element is a hidden placeholder (tabindex=-1, opacity:0),
    // return its visible clickable container instead
    // This handles React Select, MUI Select, and similar dropdown libraries
    const finalElement = this.getClickableContainer(winner.element);

    return {
      element: finalElement,
      confidence: winner.confidence,
      strategies: winner.strategies,
      candidateCount: candidateMap.size,
      ambiguous,
      duration
    };
  }

  /**
   * Execute a single strategy and return ALL matching elements (not just first)
   */
  /**
   * Check if a string is a valid CSS selector
   */
  private static isValidCSSSelector(selector: string): boolean {
    if (!selector || typeof selector !== 'string') return false;

    // Quick check for Playwright-style selectors that are NOT valid CSS
    // These should not be passed to querySelectorAll
    if (/^(role|text|label|placeholder)=/.test(selector)) {
      HybridTargetResolver._logger.warn(`[HybridTargetResolver] Skipping Playwright-style selector (not CSS): "${selector.substring(0, 50)}..."`);
      return false;
    }

    try {
      document.querySelector(selector);
      return true;
    } catch (e) {
      HybridTargetResolver._logger.warn(`[HybridTargetResolver] Invalid CSS selector: "${selector.substring(0, 50)}..."`);
      return false;
    }
  }

  private static executeStrategyMulti(strategy: string, target: HybridTarget): HTMLElement[] {
    const elements: HTMLElement[] = [];

    try {
      switch (strategy) {
        case 'selector': {
          // Use Set to deduplicate elements found by primary + fallback selectors
          const selectorElements = new Set<HTMLElement>();

          // Collect all selectors and validate them
          // Include Angular-cleaned versions as additional fallbacks
          const rawSelectors = [
            target.selector,
            ...(target.selectorFallbacks || [])
          ].filter((s): s is string => !!s);
          
          // Add Angular state-class-stripped versions as fallbacks
          const allSelectors: string[] = [];
          for (let s of rawSelectors) {
            // Fix Tailwind arbitrary value selectors: \[ in CSS needs \\[ in JS strings
            // Chrome's "Copy selector" produces .tw-h-\[calc\(100vh-400px\)\]
            // which arrives as a JS string with literal backslashes. querySelector
            // needs doubled backslashes to produce the CSS escape sequence.
            if (s.includes('\\[') || s.includes('\\(') || s.includes('\\:')) {
              const fixed = s.replace(/\\([[\](){}:!])/g, '\\\\$1');
              if (!allSelectors.includes(fixed) && this.isValidCSSSelector(fixed)) {
                allSelectors.push(fixed);
              }
            }
            if (this.isValidCSSSelector(s)) allSelectors.push(s);
            const cleaned = stripAngularStateClasses(s);
            if (cleaned !== s && this.isValidCSSSelector(cleaned) && !allSelectors.includes(cleaned)) {
              allSelectors.push(cleaned);
            }
          }

          // Query each valid selector
          for (const sel of allSelectors) {
            try {
              document.querySelectorAll(sel).forEach(el =>
                selectorElements.add(el as HTMLElement)
              );
            } catch (e) {
              // Should not happen after validation, but safety first
              HybridTargetResolver._logger.warn(`[HybridTargetResolver] querySelectorAll failed for: "${sel.substring(0, 50)}..."`);
            }
          }

          // Add deduplicated elements
          elements.push(...selectorElements);
          break;
        }

        case 'text':
          if (target.text) {
            // Don't restrict by tag - text strategy should cast a wide net
            // Let the scoring/validation determine which element is best
            elements.push(...this.findAllByText(target.text));
          }
          break;

        case 'ariaLabel':
          if (target.ariaLabel) {
            document.querySelectorAll(`[aria-label*="${CSS.escape(target.ariaLabel)}"]`)
              .forEach(el => elements.push(el as HTMLElement));
          }
          break;

        case 'placeholder':
          if (target.placeholder) {
            document.querySelectorAll(`[placeholder*="${CSS.escape(target.placeholder)}"]`)
              .forEach(el => elements.push(el as HTMLElement));
          }
          break;

        case 'role':
          if (target.role) {
            document.querySelectorAll(`[role="${target.role}"]`)
              .forEach(el => elements.push(el as HTMLElement));
          }
          break;

        case 'labelText': {
          if (target.labelText) {
            // Build a selector hint using available info
            // If we have a specific role (like combobox), use it to narrow down
            let selectorHint = target.selector || '';

            // If no selector or it's a dynamic ID (e.g., #react-select-15-input),
            // try to build a more stable selector from role/tag
            if (!selectorHint || /react-select-\d+/.test(selectorHint)) {
              if (target.role) {
                selectorHint = `[role="${target.role}"]`;
              } else if (target.tag) {
                selectorHint = target.tag;
              } else {
                selectorHint = '*';
              }
            }

            const el = this.findElementByLabelText(target.labelText, selectorHint);
            if (el) elements.push(el);
          }
          break;
        }

        case 'optionText': {
          if (target.optionText) {
            const el = this.findDropdownOptionByText(target.optionText);
            if (el) elements.push(el);
          }
          break;
        }

        case 'removeChip': {
          if (target.removeChipText) {
            const el = this.findMultiSelectChipRemoveButton(target.removeChipText);
            if (el) elements.push(el);
          }
          break;
        }

        case 'tag': {
          if (target.tag) {
            const matches = document.getElementsByTagName(target.tag);
            for (const el of matches) {
              if (!target.text || (el.textContent || '').toLowerCase().includes(target.text.toLowerCase())) {
                elements.push(el as HTMLElement);
              }
            }
          }
          break;
        }

        case 'domIndex':
          if (target.domIndex !== undefined) {
            const el = this.findByDomIndex(target.domIndex);
            if (el) elements.push(el);
          }
          break;

        case 'boundingBox': {
          if (target.boundingBox) {
            const el = this.findByBoundingBox(target.boundingBox, target.tag, target.text);
            if (el) elements.push(el);
          }
          break;
        }
      }
    } catch (e) {
      HybridTargetResolver._logger.warn(`[HybridTargetResolver] Strategy "${strategy}" threw error:`, e);
    }

    return elements;
  }

  /**
   * Find all elements matching text (not just first)
   */
  private static findAllByText(text: string, tag?: string): HTMLElement[] {
    const elements: HTMLElement[] = [];
    const seenElements = new Set<HTMLElement>(); // Use Set for O(1) lookup instead of O(n) includes()

    if (!text) return elements;

    const trimmedText = text.trim();
    if (!trimmedText) return elements;

    const tagSelector = tag || '*';
    const escapedText = this.escapeXPathString(trimmedText);

    // Try exact match first - use normalize-space(.) to match text spanning child elements
    const exactXpath = `//${tagSelector}[normalize-space(.)=${escapedText}]`;
    try {
      const exactResult = document.evaluate(
        exactXpath,
        document,
        null,
        XPathResult.ORDERED_NODE_SNAPSHOT_TYPE,
        null
      );

      for (let i = 0; i < exactResult.snapshotLength; i++) {
        const node = exactResult.snapshotItem(i) as HTMLElement;
        if (node && !this.isDropdownDisplayValue(node) && !seenElements.has(node)) {
          elements.push(node);
          seenElements.add(node);
        }
      }
    } catch (e) {
      // XPath failed, continue to partial match
    }

    // Try partial match - use normalize-space(.) to match text spanning child elements
    const partialXpath = `//${tagSelector}[contains(normalize-space(.), ${escapedText})]`;
    try {
      const partialResult = document.evaluate(
        partialXpath,
        document,
        null,
        XPathResult.ORDERED_NODE_SNAPSHOT_TYPE,
        null
      );

      for (let i = 0; i < partialResult.snapshotLength; i++) {
        const node = partialResult.snapshotItem(i) as HTMLElement;
        if (node && !this.isDropdownDisplayValue(node) && !seenElements.has(node)) {
          elements.push(node);
          seenElements.add(node);
        }
      }
    } catch (e) {
      // XPath failed
    }

    // For non-interactive elements that look like form labels, also add associated inputs.
    // This handles cases like "Choose Colleague *" where the label is a div but the actual
    // target should be the nearby input/select element.
    const additionalInputs: HTMLElement[] = [];
    for (const el of elements) {
      if (!this.isInteractiveElement(el)) {
        const nearbyInput = this.findNearbyInput(el);
        if (nearbyInput && !seenElements.has(nearbyInput)) {
          if (HybridTargetResolver._logger.isDebug) HybridTargetResolver._logger.log(`[HybridTargetResolver]   → Found nearby input for label "${el.textContent?.trim().substring(0, 30)}": <${nearbyInput.tagName.toLowerCase()}>`);
          additionalInputs.push(nearbyInput);
          seenElements.add(nearbyInput);
        }
      }
    }
    elements.push(...additionalInputs);

    // Also check placeholder matches for input/textarea
    const normalizedText = trimmedText.toLowerCase();
    const placeholderElements = document.querySelectorAll('input, textarea');
    for (const el of placeholderElements) {
      const htmlEl = el as HTMLInputElement | HTMLTextAreaElement;
      if (isSDKWidget(htmlEl) || seenElements.has(htmlEl)) continue;
      const placeholder = htmlEl.getAttribute('placeholder')?.trim().toLowerCase() || '';
      if (placeholder === normalizedText || (placeholder && placeholder.includes(normalizedText) && normalizedText.length / placeholder.length > 0.6)) {
        const rect = htmlEl.getBoundingClientRect();
        if (rect.width > 0 && rect.height > 0) {
          elements.push(htmlEl);
          seenElements.add(htmlEl);
        }
      }
    }

    return elements;
  }

  /**
   * Check if an element is natively interactive (input, button, select, textarea, a)
   */
  private static isInteractiveElement(el: HTMLElement): boolean {
    const interactiveTags = ['INPUT', 'BUTTON', 'SELECT', 'TEXTAREA', 'A'];
    if (interactiveTags.includes(el.tagName)) return true;
    if (el.getAttribute('role') === 'button' || el.getAttribute('role') === 'combobox' ||
        el.getAttribute('role') === 'textbox' || el.getAttribute('role') === 'listbox') return true;
    if (el.getAttribute('contenteditable') === 'true') return true;
    if (el.getAttribute('tabindex') !== null && el.getAttribute('tabindex') !== '-1') return true;
    return false;
  }

  /**
   * Find the nearest input/select/textarea associated with a label-like element.
   * Checks:
   * 1. Next sibling elements (label followed by input)
   * 2. Child elements (label wrapping input)
   * 3. Parent's descendant inputs (label and input as siblings within a form group)
   * 4. Elements linked via for/id attributes
   */
  private static findNearbyInput(labelElement: HTMLElement): HTMLElement | null {
    // 1. Check for HTML <label> with 'for' attribute
    if (labelElement.tagName === 'LABEL') {
      const forId = labelElement.getAttribute('for');
      if (forId) {
        const target = document.getElementById(forId);
        if (target) return target as HTMLElement;
      }
    }

    // 2. Check immediate next siblings
    let sibling = labelElement.nextElementSibling;
    for (let i = 0; i < 3 && sibling; i++) {
      const input = this.findFirstInput(sibling as HTMLElement);
      if (input) return input;
      sibling = sibling.nextElementSibling;
    }

    // 3. Check parent container for inputs (common pattern: div > label + input)
    const parent = labelElement.parentElement;
    if (parent) {
      // Look for inputs that come AFTER the label in DOM order
      const allInputs = parent.querySelectorAll('input, textarea, select, [role="combobox"], [role="listbox"], nz-select input, .ant-select input');
      for (const inp of allInputs) {
        // Make sure the input comes after the label in DOM
        if (labelElement.compareDocumentPosition(inp) & Node.DOCUMENT_POSITION_FOLLOWING) {
          const rect = (inp as HTMLElement).getBoundingClientRect();
          if (rect.width > 0 && rect.height > 0) {
            return inp as HTMLElement;
          }
        }
      }
    }

    return null;
  }

  /**
   * Find the first interactive input within an element (including itself)
   */
  private static findFirstInput(el: HTMLElement): HTMLElement | null {
    if (['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName)) return el;
    if (el.getAttribute('role') === 'combobox' || el.getAttribute('role') === 'listbox') return el;

    // Look for inputs inside (handles custom components like nz-select)
    const inner = el.querySelector('input, textarea, select, [role="combobox"], [role="listbox"]');
    if (inner) {
      const rect = (inner as HTMLElement).getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        return inner as HTMLElement;
      }
    }
    return null;
  }

  /**
   * Compute validation score based on how well element matches target metadata
   * Returns 0-100
   * 
   * Role matching strategy:
   * - If element has the CORRECT role → full points
   * - If element has a DIFFERENT role → penalty (element is likely wrong)
   * - If element has NO role but target expects one:
   *   - Container divs: small penalty (could be valid click target like React-Select control)
   *   - Other elements: moderate penalty
   * 
   * Special considerations:
   * - React-Select inputs have tabindex="-1" but are correct targets
   * - Container divs without roles are often the correct click target
   */
  private static computeValidationScore(element: HTMLElement, target: HybridTarget): number {
    let score = 0;
    let maxScore = 0;
    let hasRoleMismatch = false;
    let hasNoRoleButExpected = false;

    // Role match check
    if (target.role) {
      maxScore += 25;  // Important but not overwhelming
      const elementRole = element.getAttribute('role');
      if (elementRole === target.role) {
        score += 25;  // Full points for match
      } else if (elementRole) {
        // Element has a DIFFERENT role (e.g., role="button" vs expected "combobox")
        // This is a strong signal the element is wrong
        hasRoleMismatch = true;
      } else {
        // Element has NO role but target expects one
        // This is okay for container divs but not for other elements
        const tag = element.tagName.toLowerCase();
        const className = typeof element.className === 'string' ? element.className : '';
        const isContainer = tag === 'div' || tag === 'span' ||
          element.classList.contains('container') ||
          className.includes('control') ||
          className.includes('wrapper');

        if (isContainer) {
          // Container without role - might be React-Select control div
          // Give partial points instead of penalty
          score += 10;
        } else {
          hasNoRoleButExpected = true;
        }
      }
    }

    // Check if element looks like the correct click target for combobox
    // React-Select: the actual clickable control is often a div, not the input
    if (target.role === 'combobox') {
      const hasComboboxChild = element.querySelector('[role="combobox"]');
      if (hasComboboxChild) {
        // This element contains the combobox - it's likely the control container
        score += 10;
        maxScore += 10;
      }
    }

    // Tag match (15 points, reduced from 20)
    // Tag is less important than role for form elements
    if (target.tag) {
      maxScore += 15;
      const elementTag = element.tagName.toLowerCase();
      const targetTag = target.tag.toLowerCase();
      if (elementTag === targetTag) {
        score += 15;
      } else if (target.role === 'combobox' && (elementTag === 'input' || elementTag === 'div')) {
        // Special case: combobox containers can be div or input - both are acceptable
        score += 10;
      }
    }

    // Text match (20 points, reduced from 25)
    // Text is useful but can match wrong containers
    // CRITICAL: Partial matches (contains) must be heavily penalized to avoid
    // matching progress indicators, status text, etc that contain the target word
    if (target.text && !target.optionText) {
      maxScore += 20;
      const elementText = (element.textContent || '').toLowerCase().trim();
      const targetText = target.text.toLowerCase().trim();

      // For inputs with role=combobox, check placeholder-like sibling text
      if (target.role === 'combobox' && element.tagName.toLowerCase() === 'input') {
        // React-Select: placeholder is in a sibling div, not native placeholder attr
        const parent = element.closest('.css-6lmz24, [class*="value-container"], [class*="placeholder"]')?.parentElement;
        const placeholderDiv = parent?.querySelector('[class*="placeholder"], [id*="placeholder"]');
        const siblingText = placeholderDiv?.textContent?.toLowerCase().trim() || '';
        if (siblingText === targetText || siblingText.includes(targetText)) {
          score += 20;  // Full points - this IS the element with that placeholder
        }
      } else if (elementText === targetText) {
        score += 20;  // Exact match - full points
      } else if (elementText.includes(targetText)) {
        // Element text CONTAINS target text (e.g., "Set Up Connectivity..." contains "Connect")
        // This is risky - could be a progress indicator, status message, etc.
        const lengthRatio = targetText.length / elementText.length;

        if (lengthRatio > 0.8) {
          // Element text is only slightly longer (e.g., "Connect Now" vs "Connect")
          score += 15;
        } else if (lengthRatio > 0.5) {
          // Element text is moderately longer
          score += 8;
        } else {
          // Element text is MUCH longer (e.g., "Set Up Connectivity..." vs "Connect")
          // This is likely NOT the right element - give minimal points
          // Only give 2 points if target is a complete word in element text
          const wordBoundaryRegex = new RegExp(`\\b${HybridTargetResolver.escapeRegex(targetText)}\\b`, 'i');
          if (wordBoundaryRegex.test(elementText)) {
            score += 2;  // At least it's a complete word match
          } else {
            score += 0;  // Not even a word boundary match - no points
          }
          if (HybridTargetResolver._logger.isDebug) HybridTargetResolver._logger.log(`[HybridTargetResolver]     Text length mismatch: "${targetText}" (${targetText.length}) in "${elementText.slice(0, 30)}..." (${elementText.length}), ratio=${lengthRatio.toFixed(2)}, points=${score}`);
        }
      } else if (targetText.includes(elementText)) {
        // Target text contains element text - commonly CSS text-overflow:ellipsis truncation
        // where the DOM shows a shortened version of the plan's full text.
        const lengthRatio = elementText.length / targetText.length;
        if (targetText.startsWith(elementText) && elementText.length >= 15) {
          // Prefix match (truncation): more reliable than arbitrary substring
          score += Math.round(18 * lengthRatio); // Up to 18 of 20 points
        } else {
          // Arbitrary substring match: less reliable
          score += Math.round(10 * lengthRatio);
        }
      }
    }

    // Aria-label match (15 points)
    if (target.ariaLabel) {
      maxScore += 15;
      const elAriaLabel = element.getAttribute('aria-label') || '';
      if (elAriaLabel.toLowerCase() === target.ariaLabel.toLowerCase()) {
        score += 15;
      } else if (elAriaLabel.toLowerCase().includes(target.ariaLabel.toLowerCase())) {
        score += 10;
      }
    }

    // Placeholder match (15 points, increased from 10)
    // Enhanced to handle React-Select style placeholders
    if (target.placeholder) {
      maxScore += 15;
      const elPlaceholder = (element as HTMLInputElement).placeholder || '';
      const targetPlaceholder = target.placeholder.toLowerCase();

      if (elPlaceholder.toLowerCase().includes(targetPlaceholder)) {
        score += 15;  // Native placeholder match
      } else if (target.role === 'combobox') {
        // React-Select: Check for placeholder in sibling/child elements
        const container = element.closest('[class*="container"]') || element.parentElement;
        const placeholderEl = container?.querySelector('[class*="placeholder"], [id*="placeholder"]');
        const placeholderText = placeholderEl?.textContent?.toLowerCase().trim() || '';
        if (placeholderText.includes(targetPlaceholder)) {
          score += 15;  // React-Select placeholder match
        }
      }
    }

    // LabelText match (20 points, increased from 15)
    // This is crucial for distinguishing between multiple comboboxes
    if (target.labelText) {
      maxScore += 20;
      const associatedLabel = this.getAssociatedLabelText(element);
      if (associatedLabel) {
        const normalizedLabel = associatedLabel.toLowerCase().trim().replace(/\*$/, '').trim();
        const normalizedTarget = target.labelText.toLowerCase().trim().replace(/\*$/, '').trim();
        if (normalizedLabel === normalizedTarget) {
          score += 20;  // Exact match
        } else if (normalizedLabel.includes(normalizedTarget) || normalizedTarget.includes(normalizedLabel)) {
          score += 15;  // Partial match
        }
      }
    }

    // OptionText match (15 points)
    if (target.optionText) {
      maxScore += 15;
      const elementText = (element.textContent || '').trim();
      const normalizedText = elementText.toLowerCase();
      const normalizedTarget = target.optionText.toLowerCase().trim();
      if (normalizedText === normalizedTarget) {
        score += 15;
      } else if (normalizedText.includes(normalizedTarget)) {
        score += 10;
      }
    }

    // Normalize to 0-100
    if (maxScore === 0) return 100;  // No checks applicable
    let finalScore = Math.round((score / maxScore) * 100);

    // Apply penalties
    if (hasRoleMismatch) {
      // Element has WRONG role - strong penalty
      finalScore = Math.round(finalScore * 0.3);  // Reduce to 30% of original score
      HybridTargetResolver._logger.log(`[HybridTargetResolver]     Role mismatch penalty (30%): ${element.tagName}[role="${element.getAttribute('role')}"] vs target role="${target.role}"`);
    } else if (hasNoRoleButExpected) {
      // Element has NO role but should have one - moderate penalty
      // Exception: Don't penalize if element is inside a listbox/menu (options don't need roles)
      const inListbox = element.closest('[role="listbox"], [role="menu"], [role="tree"]');
      if (!inListbox) {
        finalScore = Math.round(finalScore * 0.7);  // Reduce to 70%
        HybridTargetResolver._logger.log(`[HybridTargetResolver]     No role penalty (70%): ${element.tagName} expected role="${target.role}"`);
      }
    }

    // BONUS: If element has tabindex="-1" but is inside a listbox, don't penalize
    // (Options in dropdowns often have tabindex=-1 for keyboard navigation)
    const tabindex = element.getAttribute('tabindex');
    if (tabindex === '-1') {
      const inSelectableContainer = element.closest('[role="listbox"], [role="menu"], [role="combobox"], .select, [class*="dropdown"]');
      if (!inSelectableContainer) {
        // Hidden input that's not in a known dropdown context - small penalty
        const tag = element.tagName.toLowerCase();
        if (tag === 'input') {
          finalScore = Math.round(finalScore * 0.85);  // 15% penalty
          HybridTargetResolver._logger.log(`[HybridTargetResolver]     tabindex=-1 input penalty (85%): likely hidden placeholder`);
        }
      }
    }

    // GENERIC: Selector context bonus
    // If we have a selector, extract the expected DOM structure and boost elements that match
    // This is a GENERIC approach that uses the selector's own structure, not hardcoded words
    if (target.selector) {
      const selectorContext = this.extractSelectorContext(target.selector);
      if (selectorContext.parentTag || selectorContext.parentSelector) {
        const contextScore = this.matchesSelectorContext(element, selectorContext);
        if (contextScore > 0.5) {
          // Element matches expected parent structure - give bonus
          finalScore = Math.min(100, Math.round(finalScore * 1.15));  // 15% bonus, capped at 100
        } else if (contextScore < 0.5 && target.text) {
          // Element doesn't match expected structure but we're doing text matching
          // Apply penalty (element might be matching text in wrong context)
          finalScore = Math.round(finalScore * 0.7);  // 30% penalty
        }
      }
    }

    // INTERACTABILITY BONUS: Prefer elements that are actually interactive
    // When we're looking for something to click (has text), interactive elements should score higher
    // This prevents selecting step labels, headers, etc. over actual buttons
    if (target.text) {
      const tag = element.tagName.toLowerCase();
      const role = element.getAttribute('role');
      const isNativelyInteractive = ['button', 'a', 'input', 'select', 'textarea'].includes(tag);
      const hasInteractiveRole = ['button', 'link', 'menuitem', 'option', 'tab', 'checkbox', 'radio'].includes(role || '');
      const hasClickHandler = element.onclick !== null || element.hasAttribute('onclick');
      const isInsideInteractive = element.closest('button, a, [role="button"], [role="link"]');

      if (isNativelyInteractive || hasInteractiveRole) {
        // Element is natively interactive - significant bonus
        finalScore = Math.min(100, Math.round(finalScore * 1.25));  // 25% bonus
        HybridTargetResolver._logger.log(`[HybridTargetResolver]     Interactive element bonus (25%): <${tag}> is natively interactive`);
      } else if (isInsideInteractive) {
        // Element is inside an interactive parent - moderate bonus
        finalScore = Math.min(100, Math.round(finalScore * 1.15));  // 15% bonus
      } else if (hasClickHandler) {
        // Element has click handler - small bonus
        finalScore = Math.min(100, Math.round(finalScore * 1.1));  // 10% bonus
      }
      // Note: No penalty for non-interactive elements as they might be valid targets
      // (e.g., divs with click handlers, custom components)
    }

    return finalScore;
  }

  /**
   * Compute final confidence score
   * 
   * Weights:
   *   - Strategy consensus: 30% (how many strategies found this element)
   *   - Validation score: 40% (how well it matches target metadata)
   *   - Visibility/interactability: 15% (can we actually click it)
   *   - Selector match bonus: 15% (selectors are most precise - recorded from actual interactions)
   */
  private static computeConfidence(candidate: ResolutionCandidate, totalStrategies: number, target: HybridTarget): number {
    // Edge case: Prevent division by zero
    if (totalStrategies === 0) {
      HybridTargetResolver._logger.warn('[HybridTargetResolver] computeConfidence: totalStrategies is 0, returning 0');
      return 0;
    }

    // Strategy consensus: What % of strategies found this element
    const consensusScore = (candidate.strategies.length / totalStrategies) * 100;

    // Validation score: How well does it match target metadata
    const validationScore = candidate.validationScore;

    // Visibility score
    let visibilityScore = 100;
    try {
      const rect = candidate.element.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) visibilityScore = 0;
      if (!candidate.interactable) visibilityScore *= 0.5;
    } catch (e) {
      // Element might have been removed, treat as not visible
      visibilityScore = 0;
    }

    // SELECTOR PRIORITY: Selectors are recorded from actual user interactions
    // They are the most precise targeting we have - if a selector matches, it's likely correct
    // BUT: If we also have target text to match, the selector should only get full bonus
    // if the element also contains/matches that text. Otherwise selector might be too generic.
    let selectorBonus = 0;
    if (candidate.strategies.includes('selector')) {
      // Check if we also have text to validate against
      if (target.text) {
        const elementText = (candidate.element.textContent || '').toLowerCase().trim();
        const targetText = target.text.toLowerCase().trim();

        if (elementText === targetText) {
          // Exact text match + selector match = highest confidence
          selectorBonus = 100;
          HybridTargetResolver._logger.log(`[HybridTargetResolver]     Selector + exact text match bonus applied`);
        } else if (elementText.includes(targetText)) {
          // Partial text match + selector match = good confidence
          selectorBonus = 70;
          HybridTargetResolver._logger.log(`[HybridTargetResolver]     Selector + partial text match bonus applied`);
        } else if (
          targetText.startsWith(elementText) &&
          elementText.length >= 15 &&
          elementText.length / targetText.length >= 0.3
        ) {
          // Element text is a truncated version of the target text (CSS text-overflow:ellipsis).
          // The longer the overlap, the more confident we are.
          const overlapRatio = elementText.length / targetText.length;
          selectorBonus = Math.round(70 * overlapRatio); // Scale up to 70 based on overlap
          HybridTargetResolver._logger.log(`[HybridTargetResolver]     Selector + truncated text match bonus: ${selectorBonus} (${Math.round(overlapRatio * 100)}% overlap)`);
        } else {
          // Selector matches but text doesn't match at all
          // This is likely a generic selector matching the wrong element
          selectorBonus = 20;  // Reduced bonus - selector alone isn't reliable here
          HybridTargetResolver._logger.log(`[HybridTargetResolver]     Selector match but NO text match - reduced bonus`);
        }
      } else {
        // No text to validate, trust the selector
        selectorBonus = 100;
        HybridTargetResolver._logger.log(`[HybridTargetResolver]     Selector match bonus applied (no text to validate)`);
      }
    }

    // Weighted average with selector priority
    const confidence = (
      consensusScore * 0.30 +      // Reduced from 40% to make room for selector bonus
      validationScore * 0.40 +
      visibilityScore * 0.15 +     // Reduced from 20%
      selectorBonus * 0.15         // 15% weight for selector match
    );

    return confidence;
  }

  /**
   * Check if element is interactable (can receive clicks/input)
   * Returns { ok: true } or { ok: false, reason: string }
   */
  private static checkInteractability(el: HTMLElement): { ok: boolean; reason?: string } {
    try {
      // Check if element is in DOM
      if (!el.isConnected) {
        return { ok: false, reason: 'Element is not connected to DOM' };
      }

      // Get computed style (might fail if element is in a different document/frame)
      let style: CSSStyleDeclaration;
      try {
        style = window.getComputedStyle(el);
      } catch (e) {
        return { ok: false, reason: 'Cannot compute style (element may be in different document)' };
      }

      // Check visibility
      if (style.display === 'none') {
        return { ok: false, reason: 'Element has display: none' };
      }
      if (style.visibility === 'hidden') {
        return { ok: false, reason: 'Element has visibility: hidden' };
      }
      const opacity = parseFloat(style.opacity);
      if (isNaN(opacity) || opacity === 0) {
        return { ok: false, reason: 'Element has opacity: 0' };
      }

      // Check pointer-events
      if (style.pointerEvents === 'none') {
        return { ok: false, reason: 'Element has pointer-events: none' };
      }

      // Check disabled state
      if ((el as HTMLInputElement).disabled) {
        return { ok: false, reason: 'Element is disabled' };
      }
      if (el.getAttribute('aria-disabled') === 'true') {
        return { ok: false, reason: 'Element has aria-disabled: true' };
      }

      // Check if rendered (offsetParent null for hidden elements)
      // Exception: fixed/sticky positioned elements have null offsetParent
      if (!el.offsetParent && style.position !== 'fixed' && style.position !== 'sticky') {
        // Check for fixed/sticky ancestor
        let parent = el.parentElement;
        let hasFixedAncestor = false;
        while (parent && parent !== document.body) {
          try {
            const parentStyle = window.getComputedStyle(parent);
            if (parentStyle.position === 'fixed' || parentStyle.position === 'sticky') {
              hasFixedAncestor = true;
              break;
            }
          } catch (e) {
            // Skip if we can't compute parent style
            break;
          }
          parent = parent.parentElement;
        }
        if (!hasFixedAncestor) {
          return { ok: false, reason: 'Element is not rendered (offsetParent is null)' };
        }
      }

      // Check dimensions (might fail if element is in a different frame)
      let rect: DOMRect;
      try {
        rect = el.getBoundingClientRect();
      } catch (e) {
        return { ok: false, reason: 'Cannot get bounding rect (element may be in different frame)' };
      }
      if (rect.width === 0 || rect.height === 0) {
        return { ok: false, reason: 'Element has zero dimensions' };
      }

      return { ok: true };
    } catch (e) {
      // Catch any unexpected errors
      HybridTargetResolver._logger.warn('[HybridTargetResolver] checkInteractability error:', e);
      return { ok: false, reason: `Error checking interactability: ${e}` };
    }
  }

  /**
   * Get available strategies based on what's provided in target
   * Priority: labelText > optionText > selector > boundingBox > placeholder > ariaLabel > text > role > tag
   * 
   * labelText and optionText are highest priority because they provide
   * precise disambiguation for dropdowns and form fields
   */
  private static getAvailableStrategies(target: HybridTarget): string[] {
    const strategies: string[] = [];
    // DISAMBIGUATION FIRST - labelText/optionText are the most reliable for form fields
    // labelText should work even without a selector - it finds elements by their associated label
    if (target.labelText) strategies.push('labelText');
    if (target.optionText) strategies.push('optionText');
    if (target.removeChipText) strategies.push('removeChip');
    // SELECTOR - reliable when available and valid
    if (target.selector) strategies.push('selector');
    // DIL strategies (most accurate positioning)
    if (target.domIndex !== undefined) strategies.push('domIndex');
    if (target.boundingBox) strategies.push('boundingBox');
    // Input-specific strategies
    if (target.placeholder) strategies.push('placeholder');
    if (target.ariaLabel) strategies.push('ariaLabel');
    // Text-based strategies (can be ambiguous)
    if (target.text) strategies.push('text');
    if (target.role) strategies.push('role');
    if (target.tag) strategies.push('tag');
    HybridTargetResolver._logger.log('[HybridTargetResolver] Auto-detected available strategies:', strategies.join(', '));
    return strategies;
  }

  /**
   * Strategy: Find by bounding box (DIL primary strategy)
   */
  private static findByDomIndex(domIndex: number): HTMLElement | null {
    if (domIndex === undefined) return null;

    // Use DOMIntrospector if available
    if (this.domIntrospector) {
      return this.domIntrospector.findElementByIndex(domIndex);
    }

    // No fallback for DOM Index - requires DOMIntrospector
    return null;
  }

  private static findByBoundingBox(
    boundingBox?: { x: number; y: number; width: number; height: number },
    expectedTag?: string,
    expectedText?: string
  ): HTMLElement | null {
    if (!boundingBox) return null;

    // Use DOMIntrospector if available
    if (this.domIntrospector) {
      return this.domIntrospector.findElementByBoundingBox(boundingBox, expectedTag, expectedText);
    }

    // Fallback: manual bounding box search
    const tolerance = 10;
    const allElements = document.body.querySelectorAll('*');
    let bestMatch: HTMLElement | null = null;
    let bestScore = 0;

    allElements.forEach((el) => {
      const htmlEl = el as HTMLElement;

      // Skip SDK widget elements
      if (isSDKWidget(htmlEl)) return;

      const rect = htmlEl.getBoundingClientRect();

      // Check overlap
      const xDiff = Math.abs(rect.x - boundingBox.x);
      const yDiff = Math.abs(rect.y - boundingBox.y);
      const wDiff = Math.abs(rect.width - boundingBox.width);
      const hDiff = Math.abs(rect.height - boundingBox.height);

      if (xDiff <= tolerance && yDiff <= tolerance && wDiff <= tolerance * 2 && hDiff <= tolerance * 2) {
        let score = 100 - xDiff - yDiff;

        if (expectedTag && htmlEl.tagName.toLowerCase() === expectedTag.toLowerCase()) {
          score += 30;
        }

        if (expectedText) {
          const text = htmlEl.innerText?.trim().toLowerCase() || '';
          if (text.includes(expectedText.toLowerCase())) {
            score += 25;
          }
        }

        if (score > bestScore) {
          bestScore = score;
          bestMatch = htmlEl;
        }
      }
    });

    return bestMatch;
  }

  /**
   * Validate that a found element matches the expected rich targeting metadata.
   * This prevents selecting the wrong element (e.g., "Labels" sidebar instead of "Label" form field).
   * 
   * Returns true if:
   * - No rich metadata provided (nothing to validate)
   * - Element matches ALL provided metadata
   * 
   * Returns false if element doesn't match ANY required metadata.
   */
  private static validateElementMatchesTarget(element: HTMLElement, target: HybridTarget): boolean {
    // If no rich metadata provided, any element is valid (backward compatibility)
    if (!target.labelText && !target.optionText && !target.role && !target.ariaLabel && !target.placeholder && !target.name && !target.tag && !target.text) {
      return true;
    }

    // Track what we validated for logging
    const checks: string[] = [];

    // NEW: Validate tag name
    if (target.tag) {
      const elementTag = element.tagName.toLowerCase();
      const targetTag = target.tag.toLowerCase();
      if (elementTag !== targetTag) {
        // Allow some flexibility: div/button containing spans, etc.
        // But strict for input types
        if (['input', 'select', 'textarea', 'button', 'a'].includes(targetTag)) {
          HybridTargetResolver._logger.log(`[HybridTargetResolver]     Validation failed: tag="${elementTag}" !== expected "${targetTag}"`);
          return false;
        }
      }
      checks.push('tag');
    }

    // NEW: Validate text content (when not using optionText)
    if (target.text && !target.optionText) {
      const elementText = (element.textContent || '').trim().toLowerCase();
      const targetText = target.text.trim().toLowerCase();

      // Require text match - either exact or contains
      const hasExactMatch = elementText === targetText;
      const hasContainsMatch = elementText.includes(targetText) || targetText.includes(elementText);

      if (!hasExactMatch && !hasContainsMatch) {
        HybridTargetResolver._logger.log(`[HybridTargetResolver]     Validation failed: text="${elementText.slice(0, 50)}" !== expected "${targetText}"`);
        return false;
      }

      // STRICT: For short target text, require high match ratio to avoid partial matches
      // "Save" should not match "Save All Changes to Draft"
      if (targetText.length <= 10 && !hasExactMatch) {
        const ratio = targetText.length / Math.max(elementText.length, 1);
        if (ratio < 0.5) {
          HybridTargetResolver._logger.log(`[HybridTargetResolver]     Validation failed: text match ratio too low (${ratio.toFixed(2)})`);
          return false;
        }
      }
      checks.push('text');
    }

    // Helper for word-boundary label matching
    const labelMatches = (elementLabel: string, targetLabel: string): boolean => {
      const normalized = elementLabel.toLowerCase().trim().replace(/\*$/, '').trim();
      const targetNormalized = targetLabel.toLowerCase().trim();
      if (normalized === targetNormalized) return true;
      // Word boundary check - "Label" should not match "Labels"
      const regex = new RegExp(`(^|\\s)${targetNormalized.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}($|\\s|[:\\*\\?\\.\\,])`, 'i');
      return regex.test(elementLabel);
    };

    // Validate ARIA role
    if (target.role) {
      const elementRole = element.getAttribute('role');
      if (elementRole !== target.role) {
        HybridTargetResolver._logger.log(`[HybridTargetResolver]     Validation failed: role="${elementRole}" !== expected "${target.role}"`);
        return false;
      }
      checks.push('role');
    }

    // Validate aria-label
    if (target.ariaLabel) {
      const elementAriaLabel = element.getAttribute('aria-label') || '';
      if (!labelMatches(elementAriaLabel, target.ariaLabel)) {
        HybridTargetResolver._logger.log(`[HybridTargetResolver]     Validation failed: aria-label="${elementAriaLabel}" !== expected "${target.ariaLabel}"`);
        return false;
      }
      checks.push('ariaLabel');
    }

    // Validate placeholder
    if (target.placeholder) {
      const elementPlaceholder = (element as HTMLInputElement).placeholder || '';
      if (!labelMatches(elementPlaceholder, target.placeholder)) {
        HybridTargetResolver._logger.log(`[HybridTargetResolver]     Validation failed: placeholder="${elementPlaceholder}" !== expected "${target.placeholder}"`);
        return false;
      }
      checks.push('placeholder');
    }

    // Validate HTML name attribute
    if (target.name) {
      const elementName = element.getAttribute('name') || '';
      if (elementName !== target.name) {
        HybridTargetResolver._logger.log(`[HybridTargetResolver]     Validation failed: name="${elementName}" !== expected "${target.name}"`);
        return false;
      }
      checks.push('name');
    }

    // Validate labelText (most complex - check associated label)
    if (target.labelText) {
      const associatedLabel = this.getAssociatedLabelText(element);
      if (!associatedLabel || !labelMatches(associatedLabel, target.labelText)) {
        HybridTargetResolver._logger.log(`[HybridTargetResolver]     Validation failed: associated label="${associatedLabel}" !== expected "${target.labelText}"`);
        return false;
      }
      HybridTargetResolver._logger.log(`[HybridTargetResolver]     ✓ Label validation passed: "${associatedLabel}" matches "${target.labelText}"`);
      checks.push('labelText');
    }

    // Validate optionText (for dropdown options)
    if (target.optionText) {
      const elementText = element.textContent?.trim() || '';
      if (!labelMatches(elementText, target.optionText)) {
        HybridTargetResolver._logger.log(`[HybridTargetResolver]     Validation failed: option text="${elementText}" !== expected "${target.optionText}"`);
        return false;
      }
      checks.push('optionText');
    }

    // Log what was validated
    if (checks.length > 0) {
      HybridTargetResolver._logger.log(`[HybridTargetResolver]     ✓ Validated: ${checks.join(', ')}`);
    }

    return true;
  }

  /**
   * Get the associated label text for an element (for validation).
   * Checks: label[for], aria-labelledby, parent container labels
   */
  private static getAssociatedLabelText(element: HTMLElement): string | null {
    // 1. Check label[for] association
    const inputId = element.getAttribute('id');
    if (inputId) {
      try {
        const labelFor = document.querySelector(`label[for="${CSS.escape(inputId)}"]`);
        if (labelFor) {
          return labelFor.textContent?.trim().replace(/\*$/, '').trim() || null;
        }
      } catch (e) { /* CSS.escape failed */ }
    }

    // 2. Check aria-labelledby
    const ariaLabelledBy = element.getAttribute('aria-labelledby');
    if (ariaLabelledBy) {
      const ids = ariaLabelledBy.split(/\s+/).filter(id => id.length > 0);
      for (const id of ids) {
        const labelEl = document.getElementById(id);
        if (labelEl) {
          return labelEl.textContent?.trim().replace(/\*$/, '').trim() || null;
        }
      }
    }

    // 3. Check parent containers for label (handles React Select, MUI, etc.)
    // These libraries often put label in a sibling div, not as a direct child
    let container = element.parentElement;
    let depth = 0;
    while (container && depth < 8) {
      // Search for labels anywhere within this container
      // IMPROVED: More comprehensive label selector to handle React/MUI structures
      const labels = container.querySelectorAll(
        'label, ' +
        '.MuiFormLabel-root, ' +
        '[class*="FormLabel"], ' +
        '[class*="form-label"], ' +
        '[class*="field-label"], ' +
        '.label'
      );

      for (const label of labels) {
        // Skip labels that are inside the element itself or contain the element
        if (element.contains(label) || label.contains(element)) continue;
        // Skip labels that have input/select inside them (wrapper labels)
        if ((label as Element).querySelector('input, select, textarea, [role="combobox"]')) continue;

        // Make sure this label is for OUR element, not another field
        const inputs = container.querySelectorAll('input, select, textarea, [role="combobox"]');

        // If there's only one input in this container, this label is for it
        if (inputs.length === 1) {
          const text = label.textContent?.trim().replace(/\*$/, '').trim();
          if (text && text.length > 0 && text.length < 50) {
            return text;
          }
        }
        // If multiple inputs, use position-based matching
        else if (this.isLabelForElement(label as HTMLElement, element, container)) {
          const text = label.textContent?.trim().replace(/\*$/, '').trim();
          if (text && text.length > 0 && text.length < 50) {
            return text;
          }
        }
      }

      container = container.parentElement;
      depth++;
    }

    return null;
  }

  /**
   * Check if a label is visually associated with an element
   * Uses position comparison - label should be above or to the left of the input
   * Also checks for common parent container patterns
   */
  private static isLabelForElement(label: HTMLElement, element: HTMLElement, container: Element): boolean {
    const labelRect = label.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();

    // Label should be above or to the left of the element (with tolerance for spacing)
    const isAbove = labelRect.bottom <= elementRect.top + 30;
    const isLeft = labelRect.right <= elementRect.left + 30 && Math.abs(labelRect.top - elementRect.top) < 60;

    if (!isAbove && !isLeft) return false;

    // Check if there's another input that's closer to this label
    const inputs = container.querySelectorAll('input, select, textarea, [role="combobox"]');
    if (inputs.length <= 1) return true;  // Only one input, must be ours

    for (const otherInput of inputs) {
      if (otherInput === element) continue;
      const otherRect = (otherInput as HTMLElement).getBoundingClientRect();

      // If label is above: check if another input is between label and our element
      if (isAbove) {
        if (otherRect.top >= labelRect.bottom - 5 && otherRect.bottom < elementRect.top) {
          return false;  // Another input is between label and element
        }
        // Check horizontal alignment when label is above
        if (Math.abs(otherRect.left - labelRect.left) < Math.abs(elementRect.left - labelRect.left) &&
          otherRect.top < elementRect.top) {
          return false;  // Another input is better horizontally aligned
        }
      }

      // If label is to the left: check if another input is closer
      if (isLeft) {
        const otherDistX = Math.abs(otherRect.left - labelRect.right);
        const elDistX = Math.abs(elementRect.left - labelRect.right);
        if (otherDistX < elDistX && Math.abs(otherRect.top - labelRect.top) < 40) {
          return false;  // Another input is closer horizontally
        }
      }
    }

    return true;
  }

  /**
   * Check if a text element (label, div, span) is positioned near an input element
   * and is more associated with it than other inputs in the container.
   * 
   * Position-based matching works by checking:
   * - isAbove: Text bottom edge is above input top edge (with tolerance)
   * - isLeft: Text right edge is to the left of input left edge
   * - No other input is "between" the text and our input
   * 
   * Pros: Works even when HTML structure varies wildly
   * Cons: Requires rendered layout (fails for hidden elements), more expensive
   */
  private static isTextNearElement(textEl: HTMLElement, inputEl: HTMLElement, allInputs: Element[]): boolean {
    const textRect = textEl.getBoundingClientRect();
    const inputRect = inputEl.getBoundingClientRect();

    // If elements aren't rendered (hidden), fall back to DOM order
    if (textRect.width === 0 || textRect.height === 0 || inputRect.width === 0 || inputRect.height === 0) {
      // DOM order fallback: text should come before input in tree order
      const position = textEl.compareDocumentPosition(inputEl);
      return (position & Node.DOCUMENT_POSITION_FOLLOWING) !== 0;
    }

    // Text should be above or to the left of the input
    const isAbove = textRect.bottom <= inputRect.top + 40;  // 40px tolerance for spacing
    const isLeft = textRect.right <= inputRect.left + 20 && Math.abs(textRect.top - inputRect.top) < 50;

    if (!isAbove && !isLeft) return false;

    // Check if there's another input that's closer to this text
    for (const otherInput of allInputs) {
      if (otherInput === inputEl) continue;
      const otherRect = (otherInput as HTMLElement).getBoundingClientRect();
      if (otherRect.width === 0 || otherRect.height === 0) continue;

      // If text is above: is another input vertically between text and our input?
      if (isAbove && otherRect.top >= textRect.bottom - 10 && otherRect.bottom < inputRect.top) {
        return false;  // Another input is between
      }

      // If text is above: is another input horizontally closer (same column)?
      if (isAbove && Math.abs(otherRect.left - textRect.left) < Math.abs(inputRect.left - textRect.left) - 20) {
        if (otherRect.top < inputRect.top && otherRect.top >= textRect.bottom - 50) {
          return false;  // Another input is closer horizontally and between
        }
      }

      // If text is left: is another input horizontally closer?
      if (isLeft) {
        const otherDistX = Math.abs(otherRect.left - textRect.right);
        const inputDistX = Math.abs(inputRect.left - textRect.right);
        if (otherDistX < inputDistX - 10 && Math.abs(otherRect.top - textRect.top) < 40) {
          return false;  // Another input is closer horizontally
        }
      }
    }

    return true;
  }

  /**
   * Check if an element is a "hidden placeholder" input that shouldn't be clicked directly.
   * React Select and similar libraries often use hidden inputs just to hold the value,
   * while the actual clickable UI is a container div.
   * 
   * Hidden placeholders typically have:
   * - tabindex="-1" (not focusable) - BUT only for inputs, not for options!
   * - opacity: 0 or visibility: hidden
   * - Very small size (min-width: 2px, etc.)
   * - position: absolute with no visible dimensions
   * 
   * IMPORTANT: Dropdown options (role="option") often have tabindex="-1" by design
   * (navigated with arrow keys, not Tab) - these are NOT hidden placeholders!
   */
  private static isHiddenPlaceholderInput(element: HTMLElement): boolean {
    // Only check inputs and elements that might be placeholder inputs
    const tagName = element.tagName.toLowerCase();
    const role = element.getAttribute('role');

    // NEVER consider dropdown options as hidden - they're meant to be clicked!
    if (role === 'option' || role === 'menuitem' || role === 'treeitem') {
      return false;
    }

    // NEVER consider elements inside an open listbox/menu as hidden
    if (element.closest('[role="listbox"], [role="menu"], [role="tree"]')) {
      return false;
    }

    if (tagName !== 'input' && !role) {
      return false;
    }

    // Check tabindex="-1" ONLY for inputs (not for options which use tabindex=-1 normally)
    const tabindex = element.getAttribute('tabindex');
    if (tabindex === '-1' && tagName === 'input') {
      HybridTargetResolver._logger.log('[HybridTargetResolver]   → Input has tabindex="-1" (hidden placeholder)');
      return true;
    }

    // Check computed style for opacity: 0 or visibility: hidden
    try {
      const computedStyle = window.getComputedStyle(element);

      // Opacity 0 means invisible
      if (computedStyle.opacity === '0') {
        HybridTargetResolver._logger.log('[HybridTargetResolver]   → Element has opacity: 0 (hidden placeholder)');
        return true;
      }

      // Visibility hidden
      if (computedStyle.visibility === 'hidden') {
        HybridTargetResolver._logger.log('[HybridTargetResolver]   → Element has visibility: hidden (hidden placeholder)');
        return true;
      }

      // Check for extremely small size (React Select uses min-width: 2px)
      const rect = element.getBoundingClientRect();
      if (rect.width <= 5 && rect.height <= 5) {
        HybridTargetResolver._logger.log(`[HybridTargetResolver]   → Element is too small (${rect.width}x${rect.height}) - hidden placeholder`);
        return true;
      }

      // Check if the element is position: absolute and mostly hidden
      if (computedStyle.position === 'absolute') {
        // Check if parent container has actual visible size but element doesn't
        const parent = element.parentElement;
        if (parent) {
          const parentRect = parent.getBoundingClientRect();
          // If parent is much larger than the input, input is probably a hidden placeholder
          if (parentRect.width > rect.width * 10 && rect.width < 20) {
            HybridTargetResolver._logger.log('[HybridTargetResolver]   → Element is tiny absolute-positioned (hidden placeholder)');
            return true;
          }
        }
      }
    } catch (e) {
      // getComputedStyle failed, assume not hidden
    }

    return false;
  }

  /**
   * Get the clickable container for a hidden placeholder input.
   * For React Select, MUI, Headless UI, etc., returns the visible control element.
   */
  private static getClickableContainer(element: HTMLElement): HTMLElement {
    // If element is not a hidden placeholder, return it as-is
    if (!this.isHiddenPlaceholderInput(element)) {
      return element;
    }

    HybridTargetResolver._logger.log('[HybridTargetResolver]   → Looking for clickable container...');

    // Look for common control patterns (dropdowns, radio/checkbox, etc.)
    // React Select: .css-*-control, [class*="control"]
    // MUI: .MuiSelect-select, .MuiInputBase-root
    // Headless UI: [role="button"], [data-headlessui-state]
    // Radix: [data-radix-collection-item]
    // Angular/Ant Design: .ant-radio-wrapper, .ant-checkbox-wrapper, nz-radio, nz-checkbox

    const controlSelectors = [
      // React Select patterns (most common)
      '[class*="-control"]',
      '[class*="control"]',
      // MUI patterns
      '.MuiSelect-select',
      '.MuiInputBase-root',
      '.MuiOutlinedInput-root',
      '.MuiRadio-root',
      '.MuiCheckbox-root',
      '.MuiFormControlLabel-root',
      // Angular / Ant Design / NZ-Zorro radio/checkbox patterns
      '.ant-radio-wrapper',
      '.ant-radio-button-wrapper',
      '.ant-checkbox-wrapper',
      'nz-radio',
      'nz-radio-button',
      'nz-checkbox',
      '[nz-radio]',
      '[nz-radio-button]',
      '[nz-checkbox]',
      // Generic patterns
      '[role="button"]',
      '[role="combobox"]:not(input)',  // Container combobox, not input
      '[role="radio"]',
      '[role="checkbox"]',
      '[aria-haspopup="listbox"]',
      // Class-based patterns
      '[class*="select-container"]',
      '[class*="dropdown-container"]',
      '[class*="picker"]',
      '[class*="radio-wrapper"]',
      '[class*="checkbox-wrapper"]',
    ];

    // First, try to find a container by going up the DOM
    let current: HTMLElement | null = element.parentElement;
    let depth = 0;

    while (current && depth < 6) {
      // Check if parent is a <label> — labels are the natural clickable surface
      // for hidden radio/checkbox inputs in all component libraries
      if (current.tagName === 'LABEL') {
        const rect = current.getBoundingClientRect();
        if (rect.width > 10 && rect.height > 10) {
          HybridTargetResolver._logger.log(`[HybridTargetResolver]   → Found clickable container (label): ${current.tagName}.${current.className?.split(' ').slice(0, 2).join('.')}`);
          return current;
        }
      }

      // Check if current element matches any control selector
      for (const selector of controlSelectors) {
        try {
          if (current.matches(selector)) {
            const rect = current.getBoundingClientRect();
            // Make sure it's actually visible (use smaller threshold for radio/checkbox)
            if (rect.width > 10 && rect.height > 10) {
              HybridTargetResolver._logger.log(`[HybridTargetResolver]   → Found clickable container: ${current.tagName}.${current.className?.split(' ').slice(0, 2).join('.')}`);
              return current;
            }
          }
        } catch (e) {
          // Invalid selector, skip
        }
      }

      // Check for generic visible container with click handlers
      const rect = current.getBoundingClientRect();
      if (rect.width > 100 && rect.height > 30) {
        // This might be the control container
        const role = current.getAttribute('role');
        const hasClickHandler = current.onclick !== null ||
          current.getAttribute('onclick') !== null ||
          current.classList.contains('clickable');
        const isVisibleControl = role === 'button' ||
          role === 'combobox' ||
          hasClickHandler ||
          // React Select container pattern
          (current.querySelector('input[role="combobox"]') === element);

        if (isVisibleControl) {
          HybridTargetResolver._logger.log(`[HybridTargetResolver]   → Found clickable container (generic): ${current.tagName}.${current.className?.split(' ').slice(0, 2).join('.')}`);
          return current;
        }
      }

      current = current.parentElement;
      depth++;
    }

    // Fallback: return the original element
    HybridTargetResolver._logger.log('[HybridTargetResolver]   → No clickable container found, using original element');
    return element;
  }

  /**
   * Strategy: Find by CSS selector
   */
  private static findBySelector(selector?: string): HTMLElement | null {
    if (!selector) return null;

    // Normalize escaped characters in selector string
    // Server may send escaped quotes (\") and escaped spaces (\ ) which need to be unescaped
    const normalizedSelector = selector
      .replace(/\\"/g, '"')      // Unescape quotes: \" -> "
      .replace(/\\ /g, ' ')      // Unescape spaces: \  -> (space)
      .replace(/\\\\/g, '\\');   // Unescape backslashes: \\ -> \

    HybridTargetResolver._logger.log(`[HybridTargetResolver]   → Normalized selector: "${normalizedSelector}"`);

    // Check for virtual attributes that need special handling
    // These are hints for the SDK, not real CSS selectors
    const optionTextMatch = normalizedSelector.match(/\[data-option-text="([^"]+)"\]/);
    if (optionTextMatch) {
      const optionText = optionTextMatch[1];
      HybridTargetResolver._logger.log(`[HybridTargetResolver]   → Virtual selector - searching for option text: "${optionText}"`);
      return this.findDropdownOptionByText(optionText);
    }

    const labelMatch = normalizedSelector.match(/\[data-label="([^"]+)"\]/);
    if (labelMatch) {
      const labelText = labelMatch[1];
      HybridTargetResolver._logger.log(`[HybridTargetResolver]   → Virtual selector - searching for combobox with label: "${labelText}"`);
      return this.findComboboxByLabel(labelText);
    }

    // Strip any remaining virtual attributes for querySelector
    const cleanSelector = normalizedSelector
      .replace(/\[data-option-text="[^"]*"\]/g, '')
      .replace(/\[data-label="[^"]*"\]/g, '')
      .replace(/\[data-index="[^"]*"\]/g, '')
      .trim();

    // CRITICAL: Detect and skip React-Select dynamic IDs
    // These IDs like "#react-select-10-input" are unstable (change per session/mount order)
    // Return null to let other strategies (labelText, placeholder, text) handle it
    const reactSelectIdPattern = /#?react-select-\d+-(input|listbox|option|placeholder|live-region)/;
    if (reactSelectIdPattern.test(cleanSelector)) {
      HybridTargetResolver._logger.log(`[HybridTargetResolver]   → Detected unstable React-Select ID pattern in "${cleanSelector}", skipping selector strategy`);
      return null;
    }

    // DIAGNOSTIC: Log when we have multiple matches for generic selectors
    // This helps identify when the bot needs to send more specific targeting info
    if (cleanSelector === 'input[role="combobox"]' || cleanSelector === '[role="combobox"]') {
      const allMatches = document.querySelectorAll(cleanSelector);
      if (allMatches.length > 1) {
        HybridTargetResolver._logger.warn(`[HybridTargetResolver]   ⚠️ AMBIGUOUS SELECTOR: "${cleanSelector}" matches ${allMatches.length} elements!`);
        HybridTargetResolver._logger.warn(`[HybridTargetResolver]   ⚠️ Bot should send more specific targeting (text, label, boundingBox, domIndex)`);
        // List all matches for debugging
        allMatches.forEach((el, i) => {
          const htmlEl = el as HTMLElement;
          const placeholder = htmlEl.getAttribute('placeholder') || '';
          const ariaLabel = htmlEl.getAttribute('aria-label') || '';
          const id = htmlEl.id || '';
          HybridTargetResolver._logger.log(`[HybridTargetResolver]      ${i}: id="${id}" placeholder="${placeholder}" aria-label="${ariaLabel}"`);
        });
      }
    }

    const el = document.querySelector(cleanSelector) as HTMLElement;
    if (!el) {
      HybridTargetResolver._logger.log(`[HybridTargetResolver]   → Selector "${cleanSelector}" matched 0 elements`);
      return null;
    }

    // FILTER: Skip accessibility-only elements (React-Select a11y spans, sr-only, etc.)
    // These elements are for screen readers and should not be click targets
    if (this.isAccessibilityOnlyElement(el)) {
      HybridTargetResolver._logger.log(`[HybridTargetResolver]   → Selector matched a11y-only element (class: ${el.className}), skipping`);
      return null;
    }

    return el;
  }

  /**
   * Check if element is an accessibility-only element (screen reader text, etc.)
   * These should be skipped as click targets - they're invisible helpers for assistive tech
   */
  private static isAccessibilityOnlyElement(element: HTMLElement): boolean {
    const className = typeof element.className === 'string' ? element.className : '';
    const text = (element.textContent || '').trim();

    // React-Select a11y spans (empty text with a11y class)
    if (className.includes('a11yText') && !text) return true;

    // Common screen-reader-only patterns
    if (className.includes('sr-only') ||
      className.includes('visually-hidden') ||
      className.includes('screenreader') ||
      className.includes('screen-reader')) return true;

    // aria-live regions with empty content (announcement containers)
    if (element.getAttribute('aria-live') && !text) return true;
    if (element.getAttribute('role') === 'log' && !text) return true;

    // Check for visually hidden styles (common a11y hiding technique)
    try {
      const style = getComputedStyle(element);
      // Clip-based hiding (classic sr-only pattern)
      if (style.position === 'absolute' &&
        (style.width === '1px' || style.height === '1px' ||
          style.clip === 'rect(0px, 0px, 0px, 0px)' ||
          style.clipPath === 'inset(50%)')) {
        return true;
      }
    } catch {
      // getComputedStyle may fail in some edge cases
    }

    return false;
  }

  /**
   * Check if an element is a dropdown "display value" (the currently selected value shown in closed dropdown)
   * These should NOT be clicked when trying to select an option - need to click actual [role="option"]
   */
  private static isDropdownDisplayValue(element: HTMLElement): boolean {
    const className = (element.className || '').toLowerCase();
    const parentClassName = (element.parentElement?.className || '').toLowerCase();

    // Common patterns for display values (NOT options)
    const displayValuePatterns = [
      'singlevalue', 'single-value',      // React Select
      'selectedvalue', 'selected-value',  // Generic
      'placeholder',                       // Placeholder text
      'value-container', 'valuecontainer', // Container for selected value
      'muiselect-select',                  // MUI Select
      'select__single-value',              // CSS Modules pattern
    ];

    for (const pattern of displayValuePatterns) {
      if (className.includes(pattern) || parentClassName.includes(pattern)) {
        return true;
      }
    }

    // Check if element is inside a closed dropdown control (not an open menu)
    const inClosedControl = element.closest('[class*="-control"]:not([class*="-menu"])');
    const notInMenu = !element.closest('[role="listbox"], [role="menu"], [class*="-menu"]');

    if (inClosedControl && notInMenu) {
      // Element is in a dropdown control but NOT in an open menu - it's a display value
      return true;
    }

    return false;
  }

  /**
   * Find a dropdown option by text in any open menu/listbox.
   * Delegates to shared dropdown-helpers module.
   */
  private static findDropdownOptionByText(text: string): HTMLElement | null {
    return _findVisibleDropdownOption(text, HybridTargetResolver.isVisible);
  }

  /**
   * Find the remove button (X) for a multi-select chip by its text content.
   * Used for React Select isMulti and similar multi-select components.
   * 
   * Structure: .multiValue > .label (contains text) + .removeButton (has X icon)
   */
  private static findMultiSelectChipRemoveButton(chipText: string): HTMLElement | null {
    const normalizedText = chipText.toLowerCase().trim();
    HybridTargetResolver._logger.log(`[HybridTargetResolver]   → Searching for multi-select chip: "${chipText}"`);

    // Multi-select chip container selectors (React Select and common patterns)
    const chipContainerSelectors = [
      '[class*="multiValue"]',           // React Select: css-tukwxc-multiValue
      '[class*="multi-value"]',          // Variations
      '[class*="chip"]',                 // MUI, etc.
      '[class*="tag"]',                  // Tag-based selectors
      '[data-tag]',                      // Data attribute pattern
    ];

    for (const containerSelector of chipContainerSelectors) {
      let chips: NodeListOf<Element>;
      try {
        chips = document.querySelectorAll(containerSelector);
      } catch (e) {
        continue;
      }

      for (const chip of chips) {
        const chipEl = chip as HTMLElement;

        // Skip invisible chips
        if (!this.isVisible(chipEl)) continue;

        // Get the text content of the chip (not the remove button)
        // Look for text in label/value container, not the whole chip
        const labelContainer = chipEl.querySelector('[class*="label"], [class*="Label"], span:first-of-type');
        const chipTextContent = labelContainer ?
          (labelContainer.textContent || '').trim().toLowerCase() :
          (chipEl.textContent || '').replace(/×|✕|x/gi, '').trim().toLowerCase();

        // Check if this chip's text matches
        if (chipTextContent === normalizedText || chipTextContent.includes(normalizedText) || normalizedText.includes(chipTextContent)) {
          HybridTargetResolver._logger.log(`[HybridTargetResolver]   → Found chip with text: "${chipEl.textContent?.trim().substring(0, 30)}..."`);

          // Find the remove button inside this chip
          // React Select uses [role="button"] or a div with X icon
          const removeButtonSelectors = [
            '[role="button"]',                    // React Select pattern
            '[class*="remove"]',                  // Generic remove class
            '[class*="Remove"]',                  // Capitalized
            '[class*="clear"]',                   // Clear button
            '[class*="delete"]',                  // Delete button
            '[aria-label*="Remove"]',             // ARIA label
            '[aria-label*="remove"]',
            '[aria-label*="Delete"]',
            '[aria-label*="delete"]',
            'button',                             // Generic button
            'svg',                                // Just the icon (if no wrapper)
          ];

          for (const btnSelector of removeButtonSelectors) {
            const removeBtn = chipEl.querySelector(btnSelector) as HTMLElement;
            if (removeBtn && this.isVisible(removeBtn)) {
              HybridTargetResolver._logger.log(`[HybridTargetResolver]   → Found remove button: <${removeBtn.tagName.toLowerCase()}> with selector "${btnSelector}"`);
              return removeBtn;
            }
          }

          // Fallback: if chip has a click handler to remove itself
          // (some implementations use the entire chip as clickable to remove)
          HybridTargetResolver._logger.log(`[HybridTargetResolver]   → No specific remove button found, returning chip container`);
          return chipEl;
        }
      }
    }

    HybridTargetResolver._logger.log(`[HybridTargetResolver]   → No chip found with text: "${chipText}"`);
    return null;
  }

  /**
   * Find combobox by associated label
   */
  private static findComboboxByLabel(labelText: string): HTMLElement | null {
    const normalizedSearch = labelText.toLowerCase().trim();

    // Helper for word-boundary matching
    const matchesLabel = (text: string): boolean => {
      const normalized = text.toLowerCase().trim().replace(/\*$/, '').trim();
      if (normalized === normalizedSearch) return true;
      // Word boundary check - avoid "Label" matching "Labels"
      const regex = new RegExp(`(^|\\s)${normalizedSearch.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}($|\\s|[:\\*\\?\\.\\,])`, 'i');
      return regex.test(text);
    };

    // Strategy 1: Find via label[for] → element[id]
    const labels = document.querySelectorAll('label[for]');
    for (const label of labels) {
      if (matchesLabel(label.textContent || '')) {
        const forId = label.getAttribute('for');
        if (forId) {
          const element = document.getElementById(forId);
          if (element && (element.getAttribute('role') === 'combobox' || element.getAttribute('aria-haspopup') === 'listbox')) {
            HybridTargetResolver._logger.log(`[HybridTargetResolver]   → Found combobox via label[for]: "${labelText}"`);
            return element as HTMLElement;
          }
        }
      }
    }

    // Strategy 2: Find via aria-labelledby
    const comboboxes = document.querySelectorAll('input[role="combobox"], button[aria-haspopup="listbox"], [role="combobox"]');
    for (const combobox of comboboxes) {
      const labelledBy = combobox.getAttribute('aria-labelledby');
      if (labelledBy) {
        const labelEl = document.getElementById(labelledBy.split(/\s+/)[0]);
        if (labelEl && matchesLabel(labelEl.textContent || '')) {
          HybridTargetResolver._logger.log(`[HybridTargetResolver]   → Found combobox via aria-labelledby: "${labelText}"`);
          return combobox as HTMLElement;
        }
      }
    }

    // Strategy 3: Container-based search (generic, not framework-specific)
    const allLabels = document.querySelectorAll('label');
    for (const label of allLabels) {
      if (matchesLabel(label.textContent || '')) {
        // Find closest form-like container
        const container = label.closest('fieldset, [role="group"], form > div, [class*="form"], [class*="field"]')
          || label.parentElement;
        if (container) {
          const combobox = container.querySelector('input[role="combobox"], button[aria-haspopup="listbox"], [role="combobox"]') as HTMLElement;
          if (combobox && this.isElementVisible(combobox)) {
            HybridTargetResolver._logger.log(`[HybridTargetResolver]   → Found combobox in label container: "${labelText}"`);
            return combobox;
          }
        }
      }
    }

    return null;
  }

  /**
   * Helper: Check if element is visible
   */
  private static isElementVisible(el: HTMLElement): boolean {
    const rect = el.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return false;
    if (!el.offsetParent) {
      try {
        const style = window.getComputedStyle(el);
        if (style.position !== 'fixed' && style.position !== 'sticky') return false;
      } catch { return false; }
    }
    return true;
  }

  /**
   * Find an element matching selector that is associated with given label text
   * This handles cases like: selector="input[role='combobox']", text="Label"
   * We want to find the combobox that has "Label" as its label, not some other element with "Label" text
   */
  private static findElementByLabelText(labelText: string, selector: string): HTMLElement | null {
    // Normalize escaped characters in selector (server may send escaped quotes/spaces)
    const normalizedSelector = selector
      .replace(/\\"/g, '"')      // Unescape quotes: \" -> "
      .replace(/\\ /g, ' ')      // Unescape spaces: \  -> (space)
      .replace(/\\\\/g, '\\');   // Unescape backslashes: \\ -> \

    // Strip virtual attributes (data-label, data-option-text, etc.) that aren't in the actual DOM
    const cleanSelector = normalizedSelector
      .replace(/\[data-label="[^"]*"\]/g, '')
      .replace(/\[data-option-text="[^"]*"\]/g, '')
      .replace(/\[data-index="[^"]*"\]/g, '')
      .trim();

    // Skip if selector is too generic to be meaningful
    // Check for generic tag-only selectors (but allow role-based selectors like [role="combobox"])
    const selectorLower = cleanSelector.toLowerCase().trim();
    const genericPatterns = ['*', 'div', 'span', 'p', 'a', 'li', 'ul', 'section', 'article'];
    const isRoleBasedSelector = /\[role=/.test(cleanSelector);
    if (genericPatterns.includes(selectorLower) && !isRoleBasedSelector) {
      return null;
    }

    // Find all elements matching the selector
    let selectorElements: NodeListOf<Element>;
    try {
      selectorElements = document.querySelectorAll(cleanSelector);
    } catch (e) {
      HybridTargetResolver._logger.warn(`[HybridTargetResolver]   → Invalid selector: "${cleanSelector}"`);
      return null;
    }

    if (selectorElements.length === 0) {
      return null;
    }

    HybridTargetResolver._logger.log(`[HybridTargetResolver]   → Looking for "${cleanSelector}" associated with label "${labelText}"`);
    HybridTargetResolver._logger.log(`[HybridTargetResolver]   → Found ${selectorElements.length} elements matching selector`);

    const normalizedLabel = labelText.toLowerCase().trim();

    // Helper: Escape regex special characters
    function escapeRegex(str: string): string {
      return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    }

    // Helper: Check if label text matches (exact or word boundary)
    const labelMatches = (text: string): boolean => {
      // Clean and normalize the label text (remove trailing * for required fields)
      const normalized = text.toLowerCase().trim().replace(/\*$/, '').trim();
      // Exact match
      if (normalized === normalizedLabel) return true;
      // Word boundary match - "Label" matches "Label:", "Label.", "Label," but not "Labels" or "MyLabel"
      // Handles common punctuation: : * ? . , ) ] } - and whitespace/end of string
      const wordBoundaryRegex = new RegExp(
        `(^|\\s)${escapeRegex(normalizedLabel)}($|\\s|[:\\*\\?\\.\\,\\)\\]\\}\\-])`,
        'i'
      );
      return wordBoundaryRegex.test(text);
    };

    // Helper: Check if element is visible
    const isVisible = (el: Element): boolean => {
      const htmlEl = el as HTMLElement;
      const rect = htmlEl.getBoundingClientRect();
      // Check if element has dimensions
      if (rect.width === 0 || rect.height === 0) return false;
      // Check offsetParent (null means hidden, except for fixed/sticky positioned elements)
      if (!htmlEl.offsetParent) {
        // Check computed style for position: fixed or sticky
        try {
          const computedStyle = window.getComputedStyle(htmlEl);
          const position = computedStyle.position;
          if (position !== 'fixed' && position !== 'sticky') {
            return false;
          }
        } catch (e) {
          return false;
        }
      }
      return true;
    };

    for (const el of selectorElements) {
      // Skip invisible elements
      if (!isVisible(el)) continue;

      // Check various ways an element can be associated with a label

      // 1. Check for label element with 'for' attribute (most reliable)
      const inputId = el.getAttribute('id');
      if (inputId) {
        try {
          const labelFor = document.querySelector(`label[for="${CSS.escape(inputId)}"]`);
          if (labelFor && labelMatches(labelFor.textContent || '')) {
            HybridTargetResolver._logger.log(`[HybridTargetResolver]   → Found via label[for] attribute`);
            return el as HTMLElement;
          }
        } catch (e) {
          // CSS.escape not supported or invalid id
        }
      }

      // 2. Check aria-labelledby (can have multiple space-separated IDs)
      const ariaLabelledBy = el.getAttribute('aria-labelledby');
      if (ariaLabelledBy) {
        // Split and filter out empty strings (from leading/trailing whitespace)
        const ids = ariaLabelledBy.split(/\s+/).filter(id => id.length > 0);
        for (const id of ids) {
          const labelEl = document.getElementById(id);
          if (labelEl && labelMatches(labelEl.textContent || '')) {
            HybridTargetResolver._logger.log(`[HybridTargetResolver]   → Found via aria-labelledby`);
            return el as HTMLElement;
          }
        }
      }

      // 3. Check aria-label
      const ariaLabel = el.getAttribute('aria-label') || '';
      if (ariaLabel && labelMatches(ariaLabel)) {
        HybridTargetResolver._logger.log(`[HybridTargetResolver]   → Found via aria-label`);
        return el as HTMLElement;
      }

      // 4. Check placeholder
      const placeholder = el.getAttribute('placeholder') || '';
      if (placeholder && labelMatches(placeholder)) {
        HybridTargetResolver._logger.log(`[HybridTargetResolver]   → Found via placeholder`);
        return el as HTMLElement;
      }

      // 5. Check parent/ancestor container for label elements
      // Walk up to find a form-field-like container
      let container = el.parentElement;
      let depth = 0;
      while (container && depth < 6) {
        // Get all inputs in this container once (for performance)
        const siblingInputs = container.querySelectorAll('input, select, textarea, [role="combobox"], [role="listbox"]');
        const siblingInputsArray = Array.from(siblingInputs);
        const elInContainer = siblingInputsArray.includes(el as Element);

        // Check if this container looks like a form field wrapper (prioritize these)
        const isFormField =
          container.classList.contains('MuiFormControl-root') ||
          container.tagName === 'FIELDSET' ||
          /form(-|_)?(field|group|control|item|row)/i.test(container.className) ||
          // React pattern: div containing both a label-like child and an input wrapper
          (container.childElementCount >= 2 && container.childElementCount <= 5);

        // PASS 1: Look for explicit label elements
        // Comprehensive selector including any class with "label" in it
        const labels = container.querySelectorAll(
          'label, ' +
          '[class*="label"]:not(input):not(select):not(textarea):not([role="combobox"]), ' +
          '.MuiFormLabel-root, ' +
          '[class*="FormLabel"], ' +
          '[class*="form-label"], ' +
          '[class*="field-label"]'
        );

        for (const label of labels) {
          // Skip labels that are inside the input element itself
          if (el.contains(label) || label.contains(el)) continue;
          // Skip labels that have input/select inside them (wrapper labels)
          if ((label as Element).querySelector('input, select, textarea, [role="combobox"]')) continue;
          // Skip if text is too long (likely a description, not a label)
          const labelText = label.textContent?.trim() || '';
          if (labelText.length > 50) continue;

          if (labelMatches(labelText)) {
            // If only one input in container, it must be ours
            if (siblingInputs.length === 1 && elInContainer) {
              HybridTargetResolver._logger.log(`[HybridTargetResolver]   → Found via label in container: "${labelText}"`);
              return el as HTMLElement;
            }
            // If multiple inputs, use position-based association
            if (elInContainer && this.isTextNearElement(label as HTMLElement, el as HTMLElement, siblingInputsArray)) {
              HybridTargetResolver._logger.log(`[HybridTargetResolver]   → Found via label position: "${labelText}"`);
              return el as HTMLElement;
            }
          }
        }

        // PASS 2: Fallback - Look for ANY text elements near the input
        // This handles divs/spans without "label" class but with the text we need
        // Only do this for containers that look like form fields (avoid false positives)
        if (isFormField || depth >= 3) {
          const textElements = container.querySelectorAll(
            ':scope > div, :scope > span, :scope > p, ' +
            ':scope > div > div, :scope > div > span, :scope > div > p'
          );

          for (const textEl of textElements) {
            // Skip if it contains inputs
            if ((textEl as Element).querySelector('input, select, textarea, [role="combobox"]')) continue;
            // Skip the input container itself
            if (textEl.contains(el) || el.contains(textEl)) continue;
            // Skip if it's likely a value display (has same text as input value)
            const elValue = (el as HTMLInputElement).value || '';
            if (elValue && textEl.textContent?.trim() === elValue) continue;

            const textContent = textEl.textContent?.trim() || '';
            // Text should be short (1-30 chars) and match our label
            if (textContent.length > 0 && textContent.length <= 30 && labelMatches(textContent)) {
              if (siblingInputs.length === 1 && elInContainer) {
                HybridTargetResolver._logger.log(`[HybridTargetResolver]   → Found via nearby text: "${textContent}"`);
                return el as HTMLElement;
              }
              // Position-based check for multiple inputs
              if (elInContainer && this.isTextNearElement(textEl as HTMLElement, el as HTMLElement, siblingInputsArray)) {
                HybridTargetResolver._logger.log(`[HybridTargetResolver]   → Found via nearby text (position): "${textContent}"`);
                return el as HTMLElement;
              }
            }
          }
        }

        container = container.parentElement;
        depth++;
      }
    }

    return null;
  }

  /**
   * Escape text for use in XPath queries
   * Handles quotes, newlines, and normalizes whitespace
   */
  private static escapeXPathString(str: string): string {
    // Normalize whitespace (XPath normalize-space does this too, but be safe)
    // Replace newlines/tabs with spaces, collapse multiple spaces
    const normalized = str.replace(/[\n\r\t]+/g, ' ').replace(/\s+/g, ' ').trim();

    // If string contains both quote types, use concat()
    if (normalized.includes('"') && normalized.includes("'")) {
      // Split by double quotes and rejoin with concat
      const parts = normalized.split('"');
      const escaped = parts.map((part, i) => {
        if (i === parts.length - 1) return `"${part}"`;
        return `"${part}", '"'`;
      }).join(', ');
      return `concat(${escaped})`;
    }
    // If string contains double quotes, wrap in single quotes
    if (normalized.includes('"')) {
      return `'${normalized}'`;
    }
    // Default: wrap in double quotes
    return `"${normalized}"`;
  }

  /**
   * Escape regex special characters in a string
   * Used to safely insert dynamic text into regex patterns
   */
  private static escapeRegex(str: string): string {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  /**
   * Strategy: Find by text content using XPath
   * Prioritizes finding the most specific (smallest/deepest) element
   * 
   * KEY PRINCIPLE: Exact text match wins over partial match
   * "Label" should match "Label", not "Labels"
   * 
   * When selector is provided, uses it to find associated elements
   */
  private static findByText(text?: string, tag?: string, selector?: string): HTMLElement | null {
    if (!text) return null;

    // Normalize the text for comparison
    const trimmedText = text.trim();
    if (!trimmedText) return null; // Handle whitespace-only input

    const normalizedText = trimmedText.toLowerCase();
    const tagSelector = tag || '*';
    const escapedText = this.escapeXPathString(trimmedText);

    // PRIORITY 0: If we have a selector hint, try to find the element associated with that label
    // This handles cases like clicking a dropdown that has "Label" as its label text
    if (selector) {
      const associatedElement = this.findElementByLabelText(trimmedText, selector);
      if (associatedElement) {
        HybridTargetResolver._logger.log(`[HybridTargetResolver]   → Found element matching selector "${selector}" associated with label "${trimmedText}"`);
        return associatedElement;
      }
    }

    // PRIORITY 1: Try EXACT text match first (prevents "Label" matching "Labels")
    // Use normalize-space(.) instead of normalize-space(text()) to match text that spans
    // across child elements (e.g., "Choose Colleague *" where "*" is in a child <span>).
    // text() only matches direct text nodes; . (dot) concatenates all descendant text.
    const exactXpath = `//${tagSelector}[normalize-space(.)=${escapedText}]`;
    HybridTargetResolver._logger.log(`[HybridTargetResolver]   → XPath exact query: ${exactXpath}`);

    let exactResult: XPathResult;
    try {
      exactResult = document.evaluate(
        exactXpath,
        document,
        null,
        XPathResult.ORDERED_NODE_SNAPSHOT_TYPE,
        null
      );
    } catch (e) {
      HybridTargetResolver._logger.warn(`[HybridTargetResolver]   → XPath query failed:`, e);
      exactResult = { snapshotLength: 0, snapshotItem: () => null } as unknown as XPathResult;
    }

    if (exactResult.snapshotLength > 0) {
      HybridTargetResolver._logger.log(`[HybridTargetResolver]   → Found ${exactResult.snapshotLength} exact match(es), checking visibility...`);

      // Find first visible element that's NOT a dropdown display value
      let firstVisibleNode: HTMLElement | null = null;
      let firstNode: HTMLElement | null = null;

      for (let i = 0; i < exactResult.snapshotLength; i++) {
        const node = exactResult.snapshotItem(i) as HTMLElement;
        if (!firstNode) firstNode = node;

        // CRITICAL: Skip dropdown display values (singleValue, selectedValue, placeholder)
        // These show the currently selected value, NOT an actual clickable option
        if (this.isDropdownDisplayValue(node)) {
          HybridTargetResolver._logger.log(`[HybridTargetResolver]   → Skipping display value (not an option): "${node.textContent?.trim().substring(0, 30)}..."`);
          continue;
        }

        const rect = node.getBoundingClientRect();
        if (rect.width > 0 && rect.height > 0) {
          firstVisibleNode = node;
          HybridTargetResolver._logger.log(`[HybridTargetResolver]   → Returning visible match ${i}: "${node.textContent?.trim().substring(0, 30)}..."`);
          break;
        }
      }

      // Return first visible, or first node if none visible
      if (firstVisibleNode) {
        return firstVisibleNode;
      }
      if (firstNode && !this.isDropdownDisplayValue(firstNode)) {
        HybridTargetResolver._logger.log(`[HybridTargetResolver]   → No visible matches, returning first (possibly hidden) element`);
        return firstNode;
      }
    }
    HybridTargetResolver._logger.log(`[HybridTargetResolver]   → No exact match, trying partial match...`);

    // PRIORITY 2: Try partial match XPath - but prefer word boundary matches and closer text matches
    // Use normalize-space(.) to also match text spanning child elements
    const partialXpath = `//${tagSelector}[contains(normalize-space(.), ${escapedText})]`;
    HybridTargetResolver._logger.log(`[HybridTargetResolver]   → XPath partial query: ${partialXpath}`);

    let partialResult: XPathResult;
    try {
      partialResult = document.evaluate(
        partialXpath,
        document,
        null,
        XPathResult.ORDERED_NODE_SNAPSHOT_TYPE,
        null
      );
    } catch (e) {
      HybridTargetResolver._logger.warn(`[HybridTargetResolver]   → XPath partial query failed:`, e);
      partialResult = { snapshotLength: 0, snapshotItem: () => null } as unknown as XPathResult;
    }

    if (partialResult.snapshotLength > 0) {
      // For partial matches, use the selector's structure to guide matching (GENERIC approach)
      // 1. Word boundary matches (e.g., "Connect" matches "Connect" button, not "Connectivity")
      // 2. Elements where text is closer to search text (higher ratio)
      // 3. Elements that match the expected parent structure from the selector
      let bestMatch: HTMLElement | null = null;
      let bestScore = -1;

      // Extract expected DOM structure from the selector (GENERIC - no hardcoded words)
      const selectorContext = selector ? this.extractSelectorContext(selector) : {};
      const hasParentContext = !!(selectorContext.parentTag || selectorContext.parentSelector);

      HybridTargetResolver._logger.log(`[HybridTargetResolver]   → Selector context: parentTag="${selectorContext.parentTag || 'none'}", expectedTag="${selectorContext.expectedTag || 'none'}"`);

      for (let i = 0; i < partialResult.snapshotLength; i++) {
        const node = partialResult.snapshotItem(i) as HTMLElement;
        const nodeText = (node.textContent || '').trim();
        // Avoid division by zero
        if (nodeText.length === 0) continue;

        const nodeTextLower = nodeText.toLowerCase();
        const ratio = normalizedText.length / nodeTextLower.length;
        const rect = node.getBoundingClientRect();

        // Skip invisible elements
        if (rect.width === 0 || rect.height === 0) continue;

        // Skip dropdown display values
        if (this.isDropdownDisplayValue(node)) continue;

        let score = ratio; // Start with text ratio

        // Check for word boundary match (prefer exact word matches)
        const wordBoundaryRegex = new RegExp(`\\b${HybridTargetResolver.escapeRegex(normalizedText)}\\b`, 'i');
        const isWordBoundary = wordBoundaryRegex.test(nodeTextLower);

        if (isWordBoundary) {
          // Word boundary match gets significant boost
          score += 0.5;
        } else if (ratio < 0.5) {
          // Very low ratio (e.g., "Connect" in "Set Up Connectivity")
          // If we have parent context from selector, only accept elements matching that context
          if (hasParentContext) {
            const contextScore = this.matchesSelectorContext(node, selectorContext);
            if (contextScore < 0.5) {
              // Doesn't match expected structure - skip
              HybridTargetResolver._logger.log(`[HybridTargetResolver]   → Skipping low ratio match that doesn't match selector context: "${nodeText.slice(0, 30)}..."`);
              continue;
            }
          } else {
            // No selector context, low ratio - skip (can't reliably identify correct element)
            HybridTargetResolver._logger.log(`[HybridTargetResolver]   → Skipping low ratio match (${ratio.toFixed(2)}): "${nodeText.slice(0, 30)}..."`);
            continue;
          }
        }

        // GENERIC: Boost elements that match the expected DOM structure from selector
        if (hasParentContext) {
          const contextScore = this.matchesSelectorContext(node, selectorContext);
          score += contextScore * 0.3; // Up to 0.3 bonus for matching selector context
        }

        if (score > bestScore) {
          bestScore = score;
          bestMatch = node;
        }
      }

      if (bestMatch) {
        HybridTargetResolver._logger.log(`[HybridTargetResolver]   → XPath partial match found (score=${bestScore.toFixed(2)})!`);
        return bestMatch;
      }
    }
    HybridTargetResolver._logger.log(`[HybridTargetResolver]   → XPath found nothing, trying fallback...`);

    // Fallback: Find the MOST SPECIFIC element containing the text
    // This prevents matching parent containers that include child text
    const allElements = document.querySelectorAll(tag || '*');
    if (HybridTargetResolver._logger.isDebug) HybridTargetResolver._logger.log(`[HybridTargetResolver]   → Fallback: checking ${allElements.length} <${tag || '*'}> elements for text "${trimmedText}"`);

    // Helper to check if element should be skipped (root containers + SDK widgets)
    const shouldSkipEl = (el: Element): boolean => {
      const htmlEl = el as HTMLElement;
      // Skip SDK widget elements
      if (isSDKWidget(htmlEl)) return true;
      // Skip root containers
      return htmlEl.id === 'root' || htmlEl.id === 'app' || htmlEl.id === '__next' ||
        htmlEl.tagName === 'BODY' || htmlEl.tagName === 'HTML';
    };

    // PRIORITY 1: Exact text match - use textContent first (faster), verify with innerText
    // Note: textContent includes hidden text, innerText is rendered text only
    // We use textContent for initial filter, then verify visibility
    for (const el of allElements) {
      if (shouldSkipEl(el)) continue;
      // Quick filter with textContent (fast)
      const textContent = el.textContent?.trim().toLowerCase() || '';
      if (textContent !== normalizedText) continue;

      // Verify element is visible and has matching rendered text
      const rect = (el as HTMLElement).getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        // Double-check with innerText for exact rendered text match
        // (only called for elements that pass textContent filter)
        const innerText = (el as HTMLElement).innerText?.trim().toLowerCase() || '';
        if (innerText === normalizedText) {
          HybridTargetResolver._logger.log(`[HybridTargetResolver]   → Found via exact text match`);
          return el as HTMLElement;
        }
      }
    }

    // PRIORITY 2: Find smallest/most specific element where textContent matches
    // Prefer elements with higher text match ratio (text length / element text length)
    let bestMatch: HTMLElement | null = null;
    let bestScore = -Infinity;

    for (const el of allElements) {
      if (shouldSkipEl(el)) continue;

      const elementText = el.textContent?.trim().toLowerCase() || '';
      // Skip empty elements to avoid division by zero
      if (elementText.length === 0) continue;

      if (elementText.includes(normalizedText)) {
        const textLengthRatio = normalizedText.length / elementText.length;
        const rect = (el as HTMLElement).getBoundingClientRect();
        const area = rect.width * rect.height;

        // Skip very large containers and invisible elements
        if (area > 0 && area < 500000) {
          // Score: prefer smaller elements with higher text match ratio
          const sizeBonus = Math.max(0, 50 - (area / 10000));
          const specificityBonus = textLengthRatio * 50;
          const score = sizeBonus + specificityBonus;

          if (score > bestScore) {
            bestScore = score;
            bestMatch = el as HTMLElement;
          }
        }
      }
    }

    if (bestMatch) {
      const rect = bestMatch.getBoundingClientRect();
      HybridTargetResolver._logger.log(`[HybridTargetResolver]   → Found via textContent (${rect.width.toFixed(0)}x${rect.height.toFixed(0)}px, score=${bestScore.toFixed(0)})`);
      return bestMatch;
    }

    // PRIORITY 3: Match input/textarea elements by placeholder attribute
    // <input> and <textarea> elements don't have text nodes, so XPath text() and textContent
    // won't find them. Check placeholder attributes as a fallback.
    const inputSelector = (tag && ['input', 'textarea'].includes(tag.toLowerCase()))
      ? tag.toLowerCase()
      : 'input, textarea';
    const placeholderElements = document.querySelectorAll(inputSelector);
    HybridTargetResolver._logger.log(`[HybridTargetResolver]   → Checking ${placeholderElements.length} input/textarea elements for placeholder "${trimmedText}"`);

    for (const el of placeholderElements) {
      const htmlEl = el as HTMLInputElement | HTMLTextAreaElement;
      if (isSDKWidget(htmlEl)) continue;

      const placeholder = htmlEl.getAttribute('placeholder')?.trim() || '';
      if (placeholder.toLowerCase() === normalizedText) {
        const rect = htmlEl.getBoundingClientRect();
        if (rect.width > 0 && rect.height > 0) {
          HybridTargetResolver._logger.log(`[HybridTargetResolver]   → Found via placeholder match: "${placeholder}"`);
          return htmlEl;
        }
      }
    }

    // Partial placeholder match (e.g., searching "Enter name" matches placeholder "Enter name here")
    let bestPlaceholderMatch: HTMLElement | null = null;
    let bestPlaceholderRatio = 0;
    for (const el of placeholderElements) {
      const htmlEl = el as HTMLInputElement | HTMLTextAreaElement;
      if (isSDKWidget(htmlEl)) continue;

      const placeholder = htmlEl.getAttribute('placeholder')?.trim().toLowerCase() || '';
      if (placeholder && placeholder.includes(normalizedText)) {
        const ratio = normalizedText.length / placeholder.length;
        if (ratio > 0.6 && ratio > bestPlaceholderRatio) {
          const rect = htmlEl.getBoundingClientRect();
          if (rect.width > 0 && rect.height > 0) {
            bestPlaceholderMatch = htmlEl;
            bestPlaceholderRatio = ratio;
          }
        }
      }
    }

    if (bestPlaceholderMatch) {
      HybridTargetResolver._logger.log(`[HybridTargetResolver]   → Found via partial placeholder match (ratio=${bestPlaceholderRatio.toFixed(2)})`);
      return bestPlaceholderMatch;
    }

    return null;
  }

  /**
   * Strategy: Find by ARIA label
   */
  private static findByAriaLabel(ariaLabel?: string): HTMLElement | null {
    if (!ariaLabel) return null;

    // Try exact match first
    let el = document.querySelector(`[aria-label="${ariaLabel}"]`) as HTMLElement;
    if (el) {
      HybridTargetResolver._logger.log(`[HybridTargetResolver]   → Found via exact aria-label match`);
      return el;
    }

    // Try case-insensitive contains match
    const elements = document.querySelectorAll('[aria-label]');
    HybridTargetResolver._logger.log(`[HybridTargetResolver]   → Checking ${elements.length} elements with aria-label`);

    for (const element of elements) {
      const label = element.getAttribute('aria-label') || '';
      if (label.toLowerCase().includes(ariaLabel.toLowerCase())) {
        HybridTargetResolver._logger.log(`[HybridTargetResolver]   → Found via partial aria-label match: "${label}"`);
        return element as HTMLElement;
      }
    }

    return null;
  }

  /**
   * Strategy: Find by placeholder text
   */
  private static findByPlaceholder(placeholder?: string): HTMLElement | null {
    if (!placeholder) return null;

    // Try exact match
    let el = document.querySelector(`[placeholder="${placeholder}"]`) as HTMLElement;
    if (el) {
      HybridTargetResolver._logger.log(`[HybridTargetResolver]   → Found via exact placeholder match`);
      return el;
    }

    // Try case-insensitive contains match
    const inputs = document.querySelectorAll('input, textarea');
    HybridTargetResolver._logger.log(`[HybridTargetResolver]   → Checking ${inputs.length} input/textarea elements`);

    for (const input of inputs) {
      const ph = input.getAttribute('placeholder') || '';
      if (ph.toLowerCase().includes(placeholder.toLowerCase())) {
        HybridTargetResolver._logger.log(`[HybridTargetResolver]   → Found via partial placeholder match: "${ph}"`);
        return input as HTMLElement;
      }
    }

    return null;
  }

  /**
   * Strategy: Find by ARIA role (with optional text filter)
   */
  private static findByRole(role?: string, text?: string): HTMLElement | null {
    if (!role) return null;

    const elements = document.querySelectorAll(`[role="${role}"]`);
    HybridTargetResolver._logger.log(`[HybridTargetResolver]   → Found ${elements.length} elements with role="${role}"`);

    if (text) {
      // Filter by text content if provided
      for (const el of elements) {
        const elementText = el.textContent?.trim() || '';
        if (elementText.toLowerCase().includes(text.toLowerCase())) {
          HybridTargetResolver._logger.log(`[HybridTargetResolver]   → Found role element with matching text`);
          return el as HTMLElement;
        }
      }
    }

    return elements[0] as HTMLElement || null;
  }

  /**
   * Strategy: Find by tag name (with optional text filter)
   */
  private static findByTag(tag?: string, text?: string): HTMLElement | null {
    if (!tag) return null;

    const elements = document.getElementsByTagName(tag);
    HybridTargetResolver._logger.log(`[HybridTargetResolver]   → Found ${elements.length} <${tag}> elements`);

    if (text) {
      const normalizedSearch = text.toLowerCase().trim();
      let exactMatch: HTMLElement | null = null;
      let closeMatch: HTMLElement | null = null;
      let closeMatchRatio = 0;

      // STRICT MATCHING: Only accept exact or very close matches
      // This prevents selecting wrong elements with similar text
      for (const el of elements) {
        const htmlEl = el as HTMLElement;

        // Skip common container elements
        if (htmlEl.id === 'root' || htmlEl.id === 'app' ||
          htmlEl.tagName === 'BODY' || htmlEl.tagName === 'HTML') {
          continue;
        }

        // Skip hidden elements (BODY already filtered above)
        if (!HybridTargetResolver.isVisible(htmlEl)) {
          continue;
        }

        // Skip elements that are too large (likely containers)
        const textLength = htmlEl.innerText?.length || 0;
        if (textLength > 200) { // Reduced from 500 to be stricter
          continue;
        }

        const elementText = htmlEl.innerText?.trim() || '';
        const normalizedElement = elementText.toLowerCase();

        // EXACT match is best
        if (normalizedElement === normalizedSearch) {
          HybridTargetResolver._logger.log(`[HybridTargetResolver]   → Found exact match: "${elementText.substring(0, 50)}..."`);
          exactMatch = htmlEl;
          break; // Exact match found, stop searching
        }

        // CLOSE match: element text must be at least 70% of search text
        // (prevents matching "Settings" when looking for "Cloud Accounts Settings Page")
        if (normalizedElement.includes(normalizedSearch)) {
          const ratio = normalizedSearch.length / normalizedElement.length;
          if (ratio > 0.7 && ratio > closeMatchRatio) {
            closeMatch = htmlEl;
            closeMatchRatio = ratio;
          }
        }
      }

      if (exactMatch) {
        return exactMatch;
      }

      if (closeMatch) {
        const matchText = closeMatch.innerText?.trim().substring(0, 50) || '';
        HybridTargetResolver._logger.log(`[HybridTargetResolver]   → Found close match (${(closeMatchRatio * 100).toFixed(0)}%): "${matchText}..."`);
        return closeMatch;
      }

      HybridTargetResolver._logger.log(`[HybridTargetResolver]   → No matching <${tag}> found for text "${text}"`);
      return null;
    }

    // No text specified - tag-only strategy is too dangerous for generic tags
    // Only use it for specific, identifiable tags (buttons, links)
    if (tag === 'div' || tag === 'span') {
      // Generic tags without text are EXTREMELY ambiguous
      // The page has hundreds of divs - don't just return the first one!
      HybridTargetResolver._logger.log(`[HybridTargetResolver]   → REFUSING to blindly return first <${tag}> - too ambiguous without text`);
      return null;
    }

    // For more specific tags (button, a, input), find first visible non-container
    for (const el of elements) {
      const htmlEl = el as HTMLElement;
      // Skip root containers
      if (htmlEl.id === 'root' || htmlEl.id === 'app' ||
        htmlEl.tagName === 'BODY' || htmlEl.tagName === 'HTML' ||
        htmlEl.classList.contains('App')) {
        continue;
      }
      // Skip very large elements (containers)
      const rect = htmlEl.getBoundingClientRect();
      if (rect.width > 500 && rect.height > 500) {
        continue;
      }
      // Must be visible
      if (rect.width > 0 && rect.height > 0) {
        return htmlEl;
      }
    }
    return null;
  }

  /**
   * Helper: Check if element is visible in viewport
   */
  static isVisible(element: HTMLElement): boolean {
    const rect = element.getBoundingClientRect();
    const style = window.getComputedStyle(element);

    return (
      rect.width > 0 &&
      rect.height > 0 &&
      style.display !== 'none' &&
      style.visibility !== 'hidden' &&
      style.opacity !== '0'
    );
  }

  /**
   * Helper: Scroll element into view smoothly
   */
  static scrollIntoView(element: HTMLElement, options?: ScrollIntoViewOptions): void {
    HybridTargetResolver._logger.log('[HybridTargetResolver] 📜 Scrolling element into view');
    element.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
      ...options
    });
  }

  /**
   * Helper: Wait for element position to stabilize (stop moving)
   * This is critical for avoiding click misses during scroll/animation
   * 
   * Returns the final stable position, or null if element disappeared
   */
  static async waitForPositionStability(
    element: HTMLElement,
    maxWaitMs: number = 1000,
    checkIntervalMs: number = 50,
    stabilityThresholdPx: number = 2
  ): Promise<DOMRect | null> {
    HybridTargetResolver._logger.log('[HybridTargetResolver] ⏳ Waiting for element position stability...');

    let lastRect = element.getBoundingClientRect();
    let stableCount = 0;
    const requiredStableChecks = 3; // Must be stable for 3 consecutive checks
    const startTime = Date.now();

    while (Date.now() - startTime < maxWaitMs) {
      await new Promise(r => setTimeout(r, checkIntervalMs));

      const currentRect = element.getBoundingClientRect();

      // Check if element is still visible
      if (currentRect.width === 0 || currentRect.height === 0) {
        HybridTargetResolver._logger.warn('[HybridTargetResolver] ⚠️ Element disappeared during stability check');
        return null;
      }

      // Check if position is stable (within threshold)
      const xDiff = Math.abs(currentRect.x - lastRect.x);
      const yDiff = Math.abs(currentRect.y - lastRect.y);

      if (xDiff < stabilityThresholdPx && yDiff < stabilityThresholdPx) {
        stableCount++;
        if (stableCount >= requiredStableChecks) {
          HybridTargetResolver._logger.log(`[HybridTargetResolver] ✅ Element position stable at (${currentRect.x.toFixed(0)}, ${currentRect.y.toFixed(0)}) after ${Date.now() - startTime}ms`);
          return currentRect;
        }
      } else {
        // Position changed, reset stability counter
        stableCount = 0;
        HybridTargetResolver._logger.log(`[HybridTargetResolver] 📍 Element still moving: (${lastRect.x.toFixed(0)}, ${lastRect.y.toFixed(0)}) → (${currentRect.x.toFixed(0)}, ${currentRect.y.toFixed(0)})`);
      }

      lastRect = currentRect;
    }

    HybridTargetResolver._logger.warn(`[HybridTargetResolver] ⚠️ Position stability timeout after ${maxWaitMs}ms`);
    return element.getBoundingClientRect();
  }

  /**
   * Helper: Re-resolve and verify element is still the same
   * Returns the fresh element reference, or null if changed/missing
   * 
   * CRITICAL: When re-resolving, we MUST exclude position-dependent properties
   * (domIndex, boundingBox) because the page may have scrolled/changed since
   * the server captured those values. However, we preserve stable disambiguators
   * (labelText, optionText, name, removeChipText, dropdownTriggerSelector, selectedValues)
   * to maintain re-resolution accuracy on complex forms.
   */
  static reResolveAndVerify(
    originalTarget: HybridTarget,
    originalElement: HTMLElement
  ): HTMLElement | null {
    // CRITICAL FIX: Create a modified target WITHOUT position-dependent properties
    // The server's domIndex and boundingBox become stale after page scroll/animation
    // But preserve stable disambiguators for better accuracy
    const positionIndependentTarget: HybridTarget = {
      text: originalTarget.text,
      selector: originalTarget.selector,
      selectorFallbacks: originalTarget.selectorFallbacks,
      tag: originalTarget.tag,
      ariaLabel: originalTarget.ariaLabel,
      placeholder: originalTarget.placeholder,
      role: originalTarget.role,
      // Preserve stable disambiguators (position-independent)
      labelText: originalTarget.labelText,
      optionText: originalTarget.optionText,
      name: originalTarget.name,
      removeChipText: originalTarget.removeChipText,
      dropdownTriggerSelector: originalTarget.dropdownTriggerSelector,
      selectedValues: originalTarget.selectedValues,
      // EXCLUDE: domIndex - becomes stale after DOM changes
      // EXCLUDE: boundingBox - becomes stale after scroll/animation
      // EXCLUDE: confidence - not relevant for re-resolution
      // Filter out position-dependent strategies (cast to handle server-sent values)
      strategy: originalTarget.strategy?.filter(s =>
        (s as string) !== 'boundingBox' && (s as string) !== 'domIndex'
      )
    };

    HybridTargetResolver._logger.log('[HybridTargetResolver] 🔄 Re-resolving with position-independent target (no boundingBox/domIndex)');

    const freshElement = this.resolveTarget(positionIndependentTarget);

    if (!freshElement) {
      HybridTargetResolver._logger.warn('[HybridTargetResolver] ⚠️ Re-resolution failed - element no longer found');
      return null;
    }

    // Verify it's the same element (or at least has same text)
    const originalText = originalElement.textContent?.trim().toLowerCase() || '';
    const freshText = freshElement.textContent?.trim().toLowerCase() || '';

    if (originalText !== freshText && originalText && freshText) {
      HybridTargetResolver._logger.warn(`[HybridTargetResolver] ⚠️ Element changed during wait: "${originalText}" → "${freshText}"`);
      // Still return the fresh element as it matches the target better
    }

    return freshElement;
  }
}

/**
 * Action Executor for automating UI interactions
 * Supports both legacy selector-based actions and new hybrid target actions
 */
/**
 * The React onClick metadata ActionExecutor reports. `type` is a literal because
 * this is the only payload shape it produces — there is no caller-supplied `type`
 * to forward, so nothing downstream can be made to mistake a click for another
 * event by reaching in from the page.
 */
export interface ReactClickMetadata {
  type: 'click';
  selector: string;
  text: string;
  tagName: string;
  timestamp: number;
  source: 'bot' | 'user';
  sourceDetail: 'react_onclick';
}

/**
 * The only two things ActionExecutor needs from the SDK.
 *
 * Passed straight down the constructor rather than parked on `window`. An earlier
 * revision exposed these as `window.__floeBotDepth` / `window.__floeSendMetadata`;
 * both were `configurable: true`, so any script sharing the customer's page could
 * redefine them — reading every click the agent made, or feeding the bot fabricated
 * clicks. A private reference has no such surface and needs no teardown.
 */
export interface SdkClickHost {
  /** True while the SDK is driving the page (`_botActionDepth > 0`). */
  isBotActive(): boolean;
  /** Report a React onClick, which never bubbles to PageTracker. */
  sendClickMetadata(data: ReactClickMetadata): void;
  /** Synchronous, bounded readable text from exactly one active dialog. */
  captureSupportRecordReadEvidence?(): string | null;
}

export class ActionExecutor {
  readonly supportRecords = new SupportRecordExecution();
  private confirmationSound: HTMLAudioElement;
  private readonly logger: Logger;
  private readonly sdkHost?: SdkClickHost;

  /** Last error message from a failed action (e.g., modal overlay blocking click) */
  public lastError: string | null = null;
  /** Bounded result witness retained only for the matching successful support click. */
  public lastSupportTransitionEvidence: import('./support-step').SupportTransitionEvidence | null = null;
  /** One-shot evidence captured inside a successful identity-bound VERIFY. */
  private readonly supportRecordReadEvidence = new Map<string, string>();
  private readonly supportFileSelectionEvidence = new Map<string, SupportFileSelectionEvidence>();

  takeSupportFileSelectionEvidence(actionId: string): SupportFileSelectionEvidence | null {
    const value = this.supportFileSelectionEvidence.get(actionId) ?? null;
    this.supportFileSelectionEvidence.delete(actionId);
    return value;
  }

  takeSupportRecordReadEvidence(actionId: string): string | null {
    const value = this.supportRecordReadEvidence.get(actionId) ?? null;
    this.supportRecordReadEvidence.delete(actionId);
    return value;
  }

  constructor(logger: Logger = defaultLogger, sdkHost?: SdkClickHost) {
    this.logger = logger;
    this.sdkHost = sdkHost;
    // Propagate the logger to HybridTargetResolver static methods.
    HybridTargetResolver.setLogger(logger);
    // Create confirmation sound
    this.confirmationSound = new Audio('data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQAAAAA=');
  }

  /**
   * Human-like click wrapper - delegates to the standalone humanLikeClick function
   * Provides a consistent interface for the ActionExecutor class
   */
  private humanLikeClick(
    targetElement: HTMLElement,
    options: {
      focus?: boolean;
      includePointerEvents?: boolean;
      nativeClickFallback?: boolean;
    } = {}
  ): HTMLElement {
    return humanLikeClick(targetElement, options);
  }

  /**
   * Execute legacy action with optional consent
   */
  async executeWithConsent(action: UIAction, requireConsent = true): Promise<boolean> {
    try {
      if (requireConsent && this.isDestructive(action)) {
        const consent = await this.getConsent(action);
        if (!consent) return false;
      }

      const result = await this.execute(action);

      if (result) {
        this.confirmationSound.play().catch(() => { });
        this.logger.log(`[ActionExecutor] Executed ${action.type} on ${action.selector}`);
      }

      return result;
    } catch (error) {
      this.logger.error('[ActionExecutor] Failed to execute action:', error);
      return false;
    }
  }

  /**
   * Execute hybrid action using multi-strategy target resolution
   */
  async executeHybridAction(action: HybridUIAction, requireConsent = true, signal?: AbortSignal): Promise<boolean> {
    this.lastError = null; // Clear previous error
    this.lastSupportTransitionEvidence = null;
    if (action.actionId) this.supportRecordReadEvidence.delete(action.actionId);
    if (action.actionId) this.supportFileSelectionEvidence.delete(action.actionId);
    if (signal?.aborted) {
      this.lastError = 'Action cancelled';
      return false;
    }
    try {
      if (action.supportContract) return await this.executeSupportStep(action, signal);
      this.logger.log('[ActionExecutor] Executing hybrid action:', action);

      // SPECIAL HANDLING: Direct URL navigation
      // Navigate actions don't need element resolution - they use a URL directly
      if (action.action === 'navigate') {
        const targetUrl = action.value;
        if (!targetUrl) {
          this.logger.error('[ActionExecutor] Navigate action missing target URL');
          this.lastError = 'Navigate action missing target URL';
          return false;
        }

        // The host in a plan's URL is where it was RECORDED, not where it replays, so
        // the path is taken and the origin is always this one. See resolveNavigationTarget.
        const target = resolveNavigationTarget(targetUrl, window.location.href);
        if (!target.ok) {
          const error = (target as { ok: false; error: string }).error;
          this.logger.error(`[ActionExecutor] Navigate blocked — ${error}`);
          this.lastError = `Navigate blocked: ${error}`;
          return false;
        }
        const resolvedUrl = target.url;

        this.logger.log(
          '[ActionExecutor] 🧭 Navigating to URL:',
          resolvedUrl,
          // Expected on a multi-tenant product that gives each customer a subdomain,
          // and the single most useful thing in the log when a plan replays somewhere
          // other than where it was recorded.
          target.hostRewritten ? '(plan host rewritten to this origin)' : '',
        );

        // SPA-friendly first, but VERIFIED — pushState satisfies any check against
        // window.location by definition, so the old `return true` here could not fail
        // even when the host router refused the route (see spa-navigation.ts).
        let spaResult: SpaNavigationResult;
        try {
          spaResult = await attemptSpaNavigation(
            createBrowserNavigationAdapter(),
            resolvedUrl,
          );
        } catch (error) {
          spaResult = {
            accepted: false,
            // Not necessarily pushState — the attempt also notifies the router, waits,
            // and may restore the URL. This string reaches the server as the failure
            // cause, so it should not name a step it cannot know threw.
            reason: `the SPA navigation attempt threw: ${error instanceof Error ? error.message : 'unknown'}`,
          };
        }

        if (spaResult.accepted) return true;

        this.logger.warn(
          `[ActionExecutor] ↪️ SPA navigation refused (${spaResult.reason}) — falling back to a full page load`,
        );
        this.lastError = `SPA navigation refused (${spaResult.reason})`;

        // A URL differing only by fragment is a same-document navigation: no request,
        // nothing rendered. reload() forces the document to actually load.
        const fragmentOnly =
          resolvedUrl.split('#')[0] === window.location.href.split('#')[0];
        window.location.href = resolvedUrl;
        if (fragmentOnly) window.location.reload();

        // Give the load a chance to unload us before answering. Returning immediately
        // would report a failure that races a navigation which is about to succeed —
        // the caller would tell the server the step failed while the browser is still
        // on its way to the page.
        await new Promise((r) => setTimeout(r, FULL_LOAD_GRACE_MS));

        // Still here, so the load was blocked (cancelled beforeunload, a
        // Content-Disposition response). The caller must hear that the app never
        // routed rather than a reflexive success.
        return false;
      }

      // SPECIAL HANDLING: Dropdown option selection
      // If we have optionText, we may need to open the dropdown first
      if (action.target.optionText && action.action === 'click') {
        this.logger.log('[ActionExecutor] 🔽 Dropdown option click detected:', action.target.optionText);

        // Try to find the option first (dropdown might already be open)
        let optionElement = HybridTargetResolver.resolveTarget(action.target);

        if (!optionElement) {
          this.logger.log('[ActionExecutor] Option not visible - dropdown may be closed, attempting to open...');

          // Try to open the dropdown using various methods
          let dropdownOpened = false;

          // Method 1: Use dropdownTriggerSelector if provided
          if (action.target.dropdownTriggerSelector) {
            this.logger.log('[ActionExecutor] Using dropdownTriggerSelector:', action.target.dropdownTriggerSelector);
            const trigger = querySelectorWithAngularFallback(action.target.dropdownTriggerSelector);
            if (trigger) {
              this.handleComboboxClick(trigger);
              dropdownOpened = true;
            }
          }

          // Method 2: Try to find combobox by labelText
          if (!dropdownOpened && action.target.labelText) {
            this.logger.log('[ActionExecutor] Trying to find dropdown by label:', action.target.labelText);
            const combobox = this.findComboboxByLabel(action.target.labelText);
            if (combobox) {
              this.handleComboboxClick(combobox);
              dropdownOpened = true;
            }
          }

          // Method 3: Try to find combobox by selector (without optionText)
          if (!dropdownOpened && action.target.selector) {
            this.logger.log('[ActionExecutor] Trying to find dropdown by selector:', action.target.selector);
            // Create a target without optionText to find the trigger
            const triggerTarget = { ...action.target, optionText: undefined };
            const triggerElement = HybridTargetResolver.resolveTarget(triggerTarget);
            if (triggerElement) {
              const combobox = this.findComboboxInput(triggerElement);
              if (combobox) {
                this.handleComboboxClick(combobox);
                dropdownOpened = true;
              }
            }
          }

          if (dropdownOpened) {
            // Wait for dropdown to open
            this.logger.log('[ActionExecutor] Waiting for dropdown to open...');
            await new Promise(resolve => setTimeout(resolve, 300));

            // Now try to find the option again
            optionElement = HybridTargetResolver.resolveTarget(action.target);

            if (!optionElement) {
              this.logger.warn('[ActionExecutor] ❌ Option still not found after opening dropdown:', action.target.optionText);
              return false;
            }
            this.logger.log('[ActionExecutor] ✅ Found option after opening dropdown');
          } else {
            this.logger.warn('[ActionExecutor] ❌ Could not open dropdown - no trigger found. Add a separate task to open the dropdown first.');
            return false;
          }
        }

        // We have the option element, click it
        if (requireConsent && this.isHybridActionDestructive(action)) {
          const consent = await this.getHybridConsent(action);
          if (!consent) return false;
        }

        HybridTargetResolver.scrollIntoView(optionElement);
        await new Promise(resolve => setTimeout(resolve, 100));
        this.performClick(optionElement);
        this.confirmationSound.play().catch(() => { });
        this.logger.log(`[ActionExecutor] ✅ Successfully clicked dropdown option: "${action.target.optionText}"`);
        return true;
      }

      // Standard resolution for non-dropdown actions
      const element = HybridTargetResolver.resolveTarget(action.target);
      if (!element) {
        this.logger.warn('[ActionExecutor] Could not resolve target:', action.target);
        return false;
      }

      // Check for consent on destructive actions
      if (requireConsent && this.isHybridActionDestructive(action)) {
        const consent = await this.getHybridConsent(action);
        if (!consent) return false;
      }

      // Execute the action, passing selector for special dropdown handling
      const selector = action.target.selector;
      const result = await this.executeOnElement(action.action, element, action.value, selector, signal);

      if (result) {
        this.confirmationSound.play().catch(() => { });
        this.logger.log(`[ActionExecutor] Successfully executed "${action.action}" on element`);
      }

      return result;
    } catch (error) {
      this.logger.error('[ActionExecutor] Failed to execute hybrid action:', error);
      this.lastError = error instanceof Error ? error.message : String(error);
      return false;
    }
  }

  /**
   * Execute action on a resolved element
   * @param actionType - Type of action (click, type, focus, etc.)
   * @param element - Target element
   * @param value - Optional value for type actions
   * @param selector - Optional selector for special dropdown handling
   */
  private async executeSupportStep(action: HybridUIAction, signal?: AbortSignal): Promise<boolean> {
    const authored = action.supportContract!;
    const authority = authored.version === 3 ? this.supportRecords.prepare(authored, action.planRunId) : null;
    const step = authority?.step ?? authored;
    const current = () => !authority || authority.current();
    const onRecord = () => !authority || authority.onRecord();
    const baseline = () => !authority || authority.baseline();
    const pageMatches = () => supportPageMatches(step) && current() && (step.action === 'navigate' || onRecord());
    const fail = (reason: string) => { this.lastError = reason; return false; };
    if (!pageMatches()) return fail('Support task does not match this tenant, record or page');
    const checkedClick = step.version >= 2 && step.action === 'click';
    const keyboardCommit = step.commitKey !== undefined;
    if (keyboardCommit && (step.version !== 3 || step.action !== 'type' || step.commitKey !== 'Enter' ||
        !supportChecksValid(step.checks))) return fail('A typed Enter commit requires explicit completion checks');
    if (step.skipIfComplete !== undefined && (!checkedClick || typeof step.skipIfComplete !== 'boolean')) {
      return fail('Conditional clicks require a compatible support contract');
    }
    if (checkedClick && !supportChecksValid(step.checks)) return fail('Support click needs valid completion checks');
    if (signal?.aborted) return fail('Support action cancelled');
    if (!supportChecksPass(step.before)) return fail('Support task prerequisites were not verified');
    if (step.action === 'file') {
      if (!action.actionId || !action.planRunId || !supportChecksValid(step.checks)) {
        return fail('File selection requires a current run and authored host checks');
      }
      const input = resolveSupportFileTarget(step);
      if (!input) return fail('File input is missing, disabled, or ambiguous');
      // A check that was already true cannot prove this file was accepted.
      if (supportChecksPass(step.checks, true)) return fail('Host acceptance was already present before file selection');
      const authorized = () => !signal?.aborted && pageMatches() && baseline();
      const canAssign = () => authorized() && resolveSupportFileTarget(step) === input && supportChecksPass(step.before) &&
        !supportChecksPass(step.checks, true);
      const afterAssign = () => authorized() && supportFileTargetStillBound(step, input);
      const result = await selectSupportFile(step, input, action.actionId, action.planRunId, signal, afterAssign, canAssign);
      if ('error' in result) return fail(result.error);
      const deadline = Date.now() + Math.min(120000, Math.max(10000, step.timeoutMs ?? 90000));
      do {
        if (!afterAssign() || input.files?.length !== 1) {
          return fail('File selection changed before host acceptance');
        }
        if (supportChecksPass(step.checks, true)) {
          const readback = input.files[0];
          if (readback.size !== result.evidence.sizeBytes ||
              !readback.name.toLowerCase().endsWith(result.evidence.extension)) {
            return fail('File selection changed before host acceptance');
          }
          const digest = await crypto.subtle.digest('SHA-256', await readback.arrayBuffer());
          const hash = [...new Uint8Array(digest)].map(byte => byte.toString(16).padStart(2, '0')).join('');
          if (!afterAssign() || input.files?.length !== 1 || input.files[0] !== readback ||
              hash !== result.evidence.sha256 || !supportChecksPass(step.checks, true)) {
            return fail('File selection changed before host acceptance');
          }
          this.supportFileSelectionEvidence.set(action.actionId, result.evidence);
          return true;
        }
        await new Promise(resolve => setTimeout(resolve, 100));
      } while (Date.now() < deadline);
      return fail('The host did not confirm the selected file');
    }
    const transitionBefore = step.resultTransition ? resultTransitionSignature(step.resultTransition) : null;
    if (step.resultTransition && !transitionBefore) return fail('Task result baseline is missing or ambiguous');
    if (checkedClick && step.skipIfComplete && supportChecksPass(step.checks, true)) {
      return baseline() ? true : fail('The recorded baseline changed before the action');
    }
    if (step.action === 'verify') {
      const deadline = Date.now() + Math.min(30000, Math.max(0, Number.isFinite(step.timeoutMs) ? step.timeoutMs! : 5000));
      do {
        if (signal?.aborted || !pageMatches()) return fail('Support verification cancelled or page changed');
        if (supportChecksPass(step.checks, true) && baseline()) {
          if (step.revealSelector) {
            // A reveal is read-only and runs only after authored identity checks pass.
            // It may move the viewport, so repeat every authority and evidence guard
            // before issuing a receipt.
            const reveal = resolveSupportReveal(step);
            if (!reveal) return fail('Support reveal target is missing, hidden, or ambiguous');
            reveal.scrollIntoView({behavior: 'auto', block: 'center', inline: 'nearest'});
            if (signal?.aborted || !pageMatches() || !supportChecksPass(step.checks, true) ||
                !baseline() || resolveSupportReveal(step) !== reveal) {
              return fail('Support verification changed during the read-only reveal');
            }
          }
          if (step.captureRecord) authority!.capture(action.actionId!);
          // The browser can swap the dialog after an awaited action. Re-check the
          // same exact authored identity synchronously immediately before and after
          // the bounded dialog capture; never fall back to a page-wide snapshot.
          if (step.captureReadEvidence) {
            const capture = this.sdkHost?.captureSupportRecordReadEvidence;
            if (!capture || !action.actionId || !current() || !onRecord() || !supportChecksPass(step.checks, true) || !baseline()) {
              return fail('Record-bound read evidence is unavailable or identity changed');
            }
            const text = capture.call(this.sdkHost);
            if (!current() || !onRecord() || !supportChecksPass(step.checks, true) || !baseline() ||
                typeof text !== 'string' || !text.trim() || text.length > 40000) {
              return fail('Record-bound read evidence changed or is invalid');
            }
            this.supportRecordReadEvidence.set(action.actionId, text);
          }
          if (step.captureBaseline) authority!.captureBaseline(action.actionId!);
          return true;
        }
        await new Promise(resolve => setTimeout(resolve, 100));
      } while (Date.now() < deadline);
      return fail('Expected page evidence was not found; completion is unverified');
    }
    if (step.action === 'navigate') {
      if (!step.destination || step.destination.includes('{{')) return fail('Missing support destination');
      // v1 plans remain usable while the bot, SDK and saved plans roll out independently.
      // v2 requires page evidence; malformed explicitly supplied v1 checks also fail closed.
      const needsEvidence = step.version >= 2 || step.checks !== undefined;
      // The destination DOM does not exist yet, so only structural and selector
      // validity can be judged here. The rendered scope is resolved by the
      // post-navigation supportChecksPass below, which fails closed when the
      // scope stays missing or ambiguous.
      if (needsEvidence && !supportChecksValid(step.checks, 'navigation')) return fail('Support navigation needs valid destination-page checks');
      // A navigation step may now start on any page of the authorized origin, so the
      // destination is the only thing deciding where the session ends up. Parse it
      // against the current origin, refuse anything that is not http(s) — a
      // `javascript:` or `data:` destination never reaches the browser's navigation
      // path — and keep the authored tenant-origin check. The step's own record
      // binding is resolved before this point, so a bound destination cannot be
      // swapped for another record here.
      let destination: URL;
      try {
        destination = new URL(step.destination, location.origin);
      } catch {
        return fail('Support navigation destination is not a valid URL');
      }
      if (destination.protocol !== 'http:' && destination.protocol !== 'https:') {
        return fail('Support navigation destination is not an http(s) URL');
      }
      if (destination.origin !== step.origin || destination.origin !== location.origin || !current()) {
        return fail('Support navigation cannot cross tenants');
      }
      if (!await this.executeHybridAction({action: 'navigate', target: {}, value: destination.href}, false, signal)) return false;
      const deadline = Date.now() + Math.min(30000, Math.max(0, Number.isFinite(step.timeoutMs) ? step.timeoutMs! : 5000));
      do {
        if (signal?.aborted || !current() || location.origin !== step.origin || !sameLocation(location.href, destination.href)) {
          return fail('Support navigation cancelled or reached a different page');
        }
        if (supportChecksPass(step.checks, needsEvidence) && onRecord()) return true;
        await new Promise(resolve => setTimeout(resolve, 100));
      } while (Date.now() < deadline);
      return fail('Destination-page evidence was not found; navigation is unverified');
    }
    const element = resolveSupportTarget(step);
    if (!element) return fail('Support target is missing, disabled, or ambiguous');
    if (keyboardCommit && (!(element instanceof HTMLInputElement) || !['text', 'search'].includes(element.type))) {
      return fail('Typed Enter requires an editable text or search input');
    }
    if (step.action === 'type' && (typeof step.value !== 'string' || step.value.includes('{{'))) {
      return fail('The requested field value has not been supplied');
    }
    const succeeded = await this.executeOnElement(step.action, element, step.value, step.selector, signal,
      () => pageMatches() && resolveSupportTarget(step) === element && supportChecksPass(step.before) &&
        baseline() &&
        !(checkedClick && step.skipIfComplete && supportChecksPass(step.checks, true)));
    if (keyboardCommit) {
      if (!succeeded || signal?.aborted || !pageMatches() || resolveSupportTarget(step) !== element ||
          !supportChecksPass(step.before) || !baseline() || !(element instanceof HTMLInputElement) || element.value !== step.value) {
        return fail('Typed field changed before its Enter commit');
      }
      // Authored opt-in only: ordinary typing never submits a host control.
      const event = {key: 'Enter', code: 'Enter', keyCode: 13, which: 13, bubbles: true, cancelable: true};
      if (element.dispatchEvent(new KeyboardEvent('keydown', event))) element.dispatchEvent(new KeyboardEvent('keypress', event));
      element.dispatchEvent(new KeyboardEvent('keyup', event));
    }
    if (!checkedClick && !keyboardCommit) return succeeded;
    // A user may expand the section while the SDK scrolls. The guard prevents
    // closing it again; the authored state still has to be verified below.
    if (!succeeded && !(step.skipIfComplete && supportChecksPass(step.checks, true))) return false;
    const deadline = Date.now() + Math.min(30000, Math.max(0, Number.isFinite(step.timeoutMs) ? step.timeoutMs! : 5000));
    let stableResultSignature: string | null = null;
    let stableResultSamples = 0;
    let stableResultStartedAt = 0;
    do {
      if (signal?.aborted || !supportPageMatches(step) || !current()) {
        return fail('Support click cancelled or page prerequisites changed');
      }
      // Before predicates authorize dispatch; the click may change them before
      // its completion checks pass (for example, adding a row before closing
      // the draft). Wait only for authored completion, with page and action
      // authority retained, and never dispatch the click again here. A host
      // refresh may temporarily remove the bound row. Require that same record
      // to return together with completion evidence, unless its removal is the
      // explicitly authored recordExit outcome.
      const completionChecksPass = supportChecksPass(step.checks, true);
      const resultSignature = step.resultTransition ? resultTransitionSignature(step.resultTransition) : null;
      if (step.resultTransition) {
        // Bind every stability sample to the authored requested scope. A prior
        // scope's stable rows cannot be credited if its card becomes active later.
        const exactEmpty = resultSignature ? resultTransitionEvidenceFromSignature(resultSignature) : null;
        const changedOrScopedEmpty = resultSignature !== transitionBefore ||
          (!!exactEmpty && exactEmpty.rows.length === 0 && exactEmpty.empty === step.resultTransition?.emptyText && exactEmpty.activeCount === 0);
        if (completionChecksPass && resultSignature && changedOrScopedEmpty) {
          stableResultSamples = resultSignature === stableResultSignature ? stableResultSamples + 1 : 1;
          stableResultSignature = resultSignature;
          if (stableResultSamples === 1) stableResultStartedAt = Date.now();
        } else {
          stableResultSignature = null;
          stableResultSamples = 0;
        }
      }
      if ((step.recordExit || onRecord()) && completionChecksPass &&
          (!step.resultTransition || (stableResultSamples >= 6 && Date.now() - stableResultStartedAt >= 500))) {
        this.lastSupportTransitionEvidence = step.resultTransition ? resultTransitionEvidenceFromSignature(stableResultSignature!) : null;
        if (step.resultTransition && !this.lastSupportTransitionEvidence) return fail('Task result evidence became ambiguous before completion');
        this.lastError = ''; return true;
      }
      await new Promise(resolve => setTimeout(resolve, 100));
    } while (Date.now() < deadline);
    return fail(step.resultTransition
      ? 'Task result did not change to one stable, unambiguous rendered state after the action'
      : 'Expected state was not found after the action; completion is unverified');
  }

  private async executeOnElement(
    actionType: string,
    element: HTMLElement,
    value?: string,
    selector?: string,
    signal?: AbortSignal,
    guard?: () => boolean
  ): Promise<boolean> {
    // Scroll into view first for better UX
    HybridTargetResolver.scrollIntoView(element);

    // Small delay for scroll animation
    await new Promise(resolve => setTimeout(resolve, 300));

    if (signal?.aborted) {
      this.lastError = 'Action cancelled';
      return false;
    }
    if (guard && !guard()) {
      this.lastError = 'Support target or prerequisites changed before the action';
      return false;
    }
    if (guard) {
      const uncovered = () => {
        const box = element.getBoundingClientRect();
        const hit = document.elementFromPoint(box.left + box.width / 2, box.top + box.height / 2);
        return !!hit && (hit === element || element.contains(hit));
      };
      // A route can render its controls while its loading overlay is fading.
      // Wait before the single action; never retry a click after dispatch.
      const deadline = Date.now() + 2000;
      while (!uncovered() && Date.now() < deadline && !signal?.aborted && guard()) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      if (signal?.aborted || !guard() || !uncovered()) {
        this.lastError = 'Support target is covered by another control';
        return false;
      }
    }

    switch (actionType) {
      case 'click':
        // Check if element is in a collapsed sidebar and expand it first
        if (!guard) await this.expandCollapsedContainer(element);

        // Use proper mouse event simulation for React/Vue/Angular compatibility
        // Pass selector for special dropdown option handling
        if (guard) this.humanLikeClick(element);
        else this.simulateClick(element, selector);
        break;

      case 'type':
        if (!element.isConnected || element.closest('[inert], [aria-disabled="true"]') ||
            element.matches(':disabled') ||
            ((element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement) && element.readOnly)) {
          this.lastError = 'Field is unavailable or read-only';
          return false;
        }
        if (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement) {
          const expectedValue = value ?? '';
          // Validate on a detached copy before changing the live field. Browsers
          // sanitize invalid number/date values to empty strings on assignment.
          const probe = element.cloneNode(false) as HTMLInputElement | HTMLTextAreaElement;
          probe.value = expectedValue;
          if (probe.value !== expectedValue || !probe.validity.valid) {
            this.lastError = 'Value does not satisfy the field constraints';
            return false;
          }
          element.focus();
          element.value = expectedValue;
          element.dispatchEvent(new Event('input', { bubbles: true }));
          element.dispatchEvent(new Event('change', { bubbles: true }));
          // Give the host's input handlers a turn to commit or reject the value.
          await new Promise(resolve => setTimeout(resolve, 0));
          if (signal?.aborted || !element.isConnected || element.value !== expectedValue || !element.validity.valid) {
            this.lastError = 'Field entry was cancelled or the page did not retain the value';
            return false;
          }
        } else if (element.getAttribute('contenteditable') === 'true') {
          // Only set textContent on actual contenteditable elements.
          // Setting textContent on a non-contenteditable container (div, body, html)
          // would destroy its entire DOM subtree — a catastrophic data loss.
          element.focus();
          element.textContent = value || '';
          element.dispatchEvent(new Event('input', { bubbles: true }));
          await new Promise(resolve => setTimeout(resolve, 0));
          if (signal?.aborted || !element.isConnected || element.textContent !== (value ?? '')) {
            this.lastError = 'Field entry was cancelled or the page did not retain the value';
            return false;
          }
        } else {
          // Safety: refuse to type into non-editable elements (divs, spans, html, etc.)
          // This prevents catastrophic DOM destruction from misresolved targets.
          console.error(
            `[ActionExecution] BLOCKED: type action on non-editable element <${element.tagName.toLowerCase()}> ` +
            `(id="${element.id}", class="${element.className?.toString().slice(0, 50)}"). ` +
            `Only input, textarea, and contenteditable elements are valid type targets.`
          );
          return false;
        }
        break;

      case 'focus':
        element.focus();
        break;

      case 'hover':
        element.dispatchEvent(new MouseEvent('mouseenter', { bubbles: true }));
        element.dispatchEvent(new MouseEvent('mouseover', { bubbles: true }));
        break;

      case 'scroll':
        // Already scrolled above, but can scroll more specifically
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        break;

      case 'highlight':
        // Highlighting is handled by OverlaySystem, not here
        // But we can add a visual effect
        element.style.outline = '3px solid #00aaff';
        element.style.outlineOffset = '2px';
        break;

      default:
        this.logger.warn('[ActionExecutor] Unknown action type:', actionType);
        return false;
    }

    return true;
  }

  /**
   * Check if element is in a collapsed sidebar/container and expand it via hover
   * Common pattern in modern UIs: sidebars that expand on hover
   */
  private async expandCollapsedContainer(element: HTMLElement): Promise<void> {
    // Find potential collapsed sidebar container
    const collapsedContainer = this.findCollapsedContainer(element);

    if (collapsedContainer) {
      this.logger.log('[ActionExecutor] Found collapsed container, hovering to expand...');

      // Get container center for hover
      const rect = collapsedContainer.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;

      const eventOptions: MouseEventInit = {
        bubbles: true,
        cancelable: true,
        view: window,
        clientX: centerX,
        clientY: centerY,
      };

      // Trigger hover events on the container
      collapsedContainer.dispatchEvent(new MouseEvent('mouseenter', eventOptions));
      collapsedContainer.dispatchEvent(new MouseEvent('mouseover', eventOptions));

      // Also move mouse to the element itself
      const elemRect = element.getBoundingClientRect();
      const elemEventOptions: MouseEventInit = {
        bubbles: true,
        cancelable: true,
        view: window,
        clientX: elemRect.left + elemRect.width / 2,
        clientY: elemRect.top + elemRect.height / 2,
      };
      element.dispatchEvent(new MouseEvent('mouseenter', elemEventOptions));
      element.dispatchEvent(new MouseEvent('mouseover', elemEventOptions));

      // Wait for expansion animation (typically 200-400ms)
      await new Promise(resolve => setTimeout(resolve, 400));

      this.logger.log('[ActionExecutor] Container expanded, proceeding with click');
    }
  }

  /**
   * Find collapsed container (sidebar) that contains the element
   * Looks for narrow containers with transition styles
   */
  private findCollapsedContainer(element: HTMLElement): HTMLElement | null {
    let current: HTMLElement | null = element.parentElement;

    while (current && current !== document.body) {
      const style = window.getComputedStyle(current);
      const width = current.getBoundingClientRect().width;

      // Check for sidebar-like characteristics:
      // - Narrow width (< 80px collapsed)
      // - Has transition on width
      // - Fixed or relative positioning
      // - Full height
      const hasTransition = style.transition.includes('width') ||
        style.transitionProperty.includes('width') ||
        style.transitionProperty === 'all';
      const isNarrow = width < 80;
      const isFullHeight = current.offsetHeight > window.innerHeight * 0.7;
      const currentClassName = typeof current.className === 'string' ? current.className : '';
      const hasSidebarClasses = currentClassName.includes('sidebar') ||
        currentClassName.includes('nav') ||
        currentClassName.includes('menu');

      // Check for common sidebar patterns
      if (isNarrow && (hasTransition || hasSidebarClasses) && isFullHeight) {
        this.logger.log('[ActionExecutor] Detected collapsed container:', {
          width,
          hasTransition,
          isFullHeight,
          classes: current.className.substring(0, 100)
        });
        return current;
      }

      current = current.parentElement;
    }

    return null;
  }

  /**
   * Simulate a real click event for React/Vue/Angular compatibility
   * Simple element.click() doesn't trigger synthetic event handlers properly
   */
  private simulateClick(element: HTMLElement, selector?: string): void {
    // SPECIAL HANDLING: Dropdown option with text matching
    // Check if selector contains data-option-text for dropdown option matching
    if (selector) {
      const optionText = this.parseOptionTextFromSelector(selector);
      if (optionText) {
        this.logger.log('[ActionExecutor] 🎯 Detected dropdown option selector, searching for:', optionText);
        const option = this.findDropdownOption(optionText);
        if (option) {
          this.logger.log('[ActionExecutor] Found option by text, clicking it');
          this.performClick(option);
          return;
        }
        this.logger.log('[ActionExecutor] Option not found by text, trying direct click');
      }

      // Check if selector contains data-label for labeled combobox
      const labelText = this.parseLabelFromSelector(selector);
      if (labelText) {
        this.logger.log('[ActionExecutor] 🎯 Detected labeled combobox selector:', labelText);
        const combobox = this.findComboboxByLabel(labelText);
        if (combobox) {
          this.logger.log('[ActionExecutor] Found combobox by label, opening it');
          this.handleComboboxClick(combobox);
          return;
        }
        this.logger.log('[ActionExecutor] Combobox not found by label, trying direct match');
      }
    }

    // SPECIAL HANDLING: Combobox/dropdown components need special interaction
    // Supports: React Select, MUI Select, Headless UI, Radix, Downshift, native combobox
    const comboboxInput = this.findComboboxInput(element);
    if (comboboxInput) {
      this.logger.log('[ActionExecutor] 🎯 Detected combobox/dropdown - using special handling');
      this.handleComboboxClick(comboboxInput);
      return;
    }

    // Check if element is a dropdown option
    if (this.isDropdownOption(element)) {
      this.logger.log('[ActionExecutor] 🎯 Element is dropdown option - using React onClick + DOM events');

      // React Select uses React synthetic events, so we need to trigger React onClick
      const hasReactHandler = this.triggerReactOnClick(element);
      this.logger.log(`[ActionExecutor] React onClick triggered: ${hasReactHandler}`);

      // Also dispatch DOM events as backup (some libraries use both)
      this.performClick(element);
      return;
    }

    // If element is a non-interactive tag (span, label, etc.), find clickable parent
    const clickTarget = this.findClickableElement(element);

    const rect = clickTarget.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    // Focus first (important for some React components)
    if (clickTarget.focus) {
      clickTarget.focus();
    }

    // STRATEGY: Check for React onClick FIRST
    // If found, use ONLY React onClick (avoids double-click issues)
    // If not found, fall back to regular DOM events

    const hasReactOnClick = this.hasReactOnClickHandler(clickTarget);

    if (hasReactOnClick) {
      this.logger.log('[ActionExecutor] 🎯 Element has React onClick - using direct call only');
      const reactClickSuccess = this.triggerReactOnClick(clickTarget);
      if (reactClickSuccess) {
        // Send click metadata since React onClick doesn't bubble to PageTracker
        // Pass BOTH the clickTarget (for selector) AND original element (for text)
        this.sendClickMetadata(clickTarget, element);
        this.logger.log('[ActionExecutor] ✅ React onClick executed at', centerX, centerY, 'on', clickTarget.tagName);
        if (clickTarget !== element) {
          this.logger.log('[ActionExecutor] Note: Clicked parent', clickTarget.tagName, 'instead of', element.tagName);
        }
        return; // Don't fall through to DOM events
      }
      this.logger.log('[ActionExecutor] ⚠️ React onClick found but call failed, falling back to DOM events');
    }

    // No React onClick found (or it failed) - use human-like click
    // This uses elementFromPoint to dispatch events to whatever is actually at the coordinates
    // (mimics real human click behavior)
    this.logger.log('[ActionExecutor] Using humanLikeClick (no React onClick or fallback)');

    const actualClickTarget = this.humanLikeClick(clickTarget, {
      focus: true,
      includePointerEvents: true,
      nativeClickFallback: false  // Don't double-click
    });

    if (actualClickTarget !== element && actualClickTarget !== clickTarget) {
      this.logger.log('[ActionExecutor] Note: humanLikeClick hit', actualClickTarget.tagName, 
        'instead of element', element.tagName, 'or clickTarget', clickTarget.tagName);
    }
  }

  /**
   * Detect if element is part of a combobox/dropdown component and find the trigger
   * Works with: React Select, MUI Select, Headless UI, Radix, Downshift, native combobox
   * Uses ARIA attributes for reliable detection across libraries
   * Returns HTMLElement (can be input or button) or null
   * 
   * IMPORTANT: Only returns a combobox if the clicked element is DIRECTLY part of
   * the combobox structure (control, value, indicators), not just any element
   * that happens to be in a container with a combobox.
   */
  private findComboboxInput(element: HTMLElement): HTMLElement | null {
    // Strategy 1: Check if element itself is a combobox input
    if (element.getAttribute('role') === 'combobox') {
      this.logger.log('[ActionExecutor] Element is combobox');
      return element;
    }

    // Strategy 2: Check if element has aria-haspopup (common for dropdown triggers)
    if (element.getAttribute('aria-haspopup') === 'listbox' ||
      element.getAttribute('aria-haspopup') === 'true') {
      const input = element.querySelector('input[role="combobox"]') as HTMLElement;
      if (input) {
        this.logger.log('[ActionExecutor] Found combobox via aria-haspopup container');
        return input;
      }
      // Element itself might be the trigger (button with aria-haspopup)
      if (element.tagName === 'BUTTON') {
        this.logger.log('[ActionExecutor] Element is button with aria-haspopup');
        return element;
      }
    }

    // Strategy 3: Walk up the tree looking for DIRECT combobox membership
    // Only trigger if clicked element is actually part of the combobox control
    let current: HTMLElement | null = element;
    let depth = 0;

    // Maximum depth to search - combobox controls are usually shallow structures
    const MAX_DEPTH = 5;

    while (current && depth < MAX_DEPTH) {
      // Handle SVG elements which have SVGAnimatedString for className
      const className = typeof current.className === 'string' ? current.className : '';

      // Check if we're inside a React Select CONTROL (not just container)
      // The control is the clickable part, container is the wrapper
      const isReactSelectControl = className.includes('css-') && className.includes('control');
      const isReactSelectValueContainer = className.includes('css-') && className.includes('ValueContainer');
      const isReactSelectIndicator = className.includes('css-') && className.includes('Indicator');

      // Check for MUI Select trigger area
      const isMuiSelectTrigger = className.includes('MuiSelect-select') ||
        className.includes('MuiAutocomplete-inputRoot');

      // Check for Headless/Radix trigger
      const isHeadlessRadixTrigger = (className.includes('headlessui') || className.includes('radix')) &&
        (className.includes('trigger') || className.includes('button'));

      // Check for generic patterns that indicate DIRECT membership
      const isDirectDropdownPart =
        isReactSelectControl ||
        isReactSelectValueContainer ||
        isReactSelectIndicator ||
        isMuiSelectTrigger ||
        isHeadlessRadixTrigger ||
        // Generic: element is inside something explicitly named control/trigger
        (className.includes('control') && current.querySelector('input[role="combobox"]')) ||
        (className.includes('trigger') && current.getAttribute('aria-haspopup'));

      if (isDirectDropdownPart) {
        // Found direct dropdown part - find the combobox input
        const input = current.querySelector('input[role="combobox"]') as HTMLElement;
        if (input) {
          this.logger.log('[ActionExecutor] Found combobox input via control element');
          return input;
        }

        // Check parent for input (input might be sibling of control)
        const parent = current.parentElement;
        if (parent) {
          const siblingInput = parent.querySelector('input[role="combobox"]') as HTMLElement;
          if (siblingInput) {
            this.logger.log('[ActionExecutor] Found combobox input in parent container');
            return siblingInput;
          }
        }

        // Some libraries use button as trigger
        const button = current.querySelector('button[aria-haspopup="listbox"]') as HTMLElement;
        if (button) {
          this.logger.log('[ActionExecutor] Found listbox trigger button');
          return button;
        }
      }

      current = current.parentElement;
      depth++;
    }

    // Not part of a combobox - return null to use normal click handling
    return null;
  }

  /**
   * Parse custom selector attributes for dropdown handling
   * Selectors like: [role="option"][data-option-text="Some Text"]
   * Returns the text to search for, or null
   * 
   * Handles CSS.escape() escaped characters from floe-recorder.js:
   * - Escaped quotes: \" -> "
   * - Escaped spaces: \  -> (space)
   */
  private parseOptionTextFromSelector(selector: string): string | null {
    // First normalize escaped characters (from CSS.escape and JSON serialization)
    const normalizedSelector = selector
      .replace(/\\"/g, '"')      // Unescape quotes: \" -> "
      .replace(/\\ /g, ' ')      // Unescape spaces: \  -> (space)
      .replace(/\\\\/g, '\\');   // Unescape backslashes: \\ -> \

    const match = normalizedSelector.match(/\[data-option-text="([^"]+)"\]/);
    if (match) {
      return match[1];
    }
    return null;
  }

  /**
   * Parse label from custom selector for combobox identification
   * Selectors like: input[role="combobox"][data-label="Label Text"]
   * 
   * Handles CSS.escape() escaped characters from floe-recorder.js:
   * - Escaped quotes: \" -> "
   * - Escaped spaces: \  -> (space)
   */
  private parseLabelFromSelector(selector: string): string | null {
    // First normalize escaped characters (from CSS.escape and JSON serialization)
    const normalizedSelector = selector
      .replace(/\\"/g, '"')      // Unescape quotes: \" -> "
      .replace(/\\ /g, ' ')      // Unescape spaces: \  -> (space)
      .replace(/\\\\/g, '\\');   // Unescape backslashes: \\ -> \

    const match = normalizedSelector.match(/\[data-label="([^"]+)"\]/);
    if (match) {
      return match[1];
    }
    return null;
  }

  /**
   * Strip virtual attributes from selector for querySelector compatibility
   * Removes: [data-option-text="..."], [data-label="..."], [data-index="..."]
   * 
   * First normalizes escaped characters from CSS.escape/JSON serialization.
   */
  private stripVirtualAttributes(selector: string): string {
    // First normalize escaped characters
    const normalizedSelector = selector
      .replace(/\\"/g, '"')      // Unescape quotes: \" -> "
      .replace(/\\ /g, ' ')      // Unescape spaces: \  -> (space)
      .replace(/\\\\/g, '\\');   // Unescape backslashes: \\ -> \

    return normalizedSelector
      .replace(/\[data-option-text="[^"]*"\]/g, '')
      .replace(/\[data-label="[^"]*"\]/g, '')
      .replace(/\[data-index="[^"]*"\]/g, '')
      .trim();
  }

  /**
   * Find combobox by associated label text
   */
  private findComboboxByLabel(labelText: string): HTMLElement | null {
    // Find all labels and match by text
    const labels = document.querySelectorAll('label');
    for (const label of labels) {
      const text = label.textContent?.trim().replace(/\*$/, '').trim();
      if (text === labelText || text?.includes(labelText)) {
        // Found label - now find the combobox in the same container
        const container = label.closest('div[class*="container"], div[aria-label], .MuiFormControl-root, form > div');
        if (container) {
          const combobox = container.querySelector('input[role="combobox"], button[aria-haspopup="listbox"]') as HTMLElement;
          if (combobox) {
            this.logger.log('[ActionExecutor] Found combobox by label:', labelText);
            return combobox;
          }
        }
      }
    }
    return null;
  }

  /**
   * Handle click on combobox/dropdown component
   * Opens the dropdown by focusing the input and dispatching proper events
   * Works with various dropdown libraries
   * 
   * Uses humanLikeClick to dispatch events to whatever is actually at the center
   * of the control - this is crucial for React-Select and similar components where
   * the clickable area might be a nested element (dropdown indicator, placeholder, etc.)
   */
  private handleComboboxClick(trigger: HTMLElement): void {
    this.logger.log('[ActionExecutor] Opening combobox/dropdown with humanLikeClick');

    // Find the control/container element for clicking
    const control = trigger.closest('[class*="control"]') ||
      trigger.closest('[class*="trigger"]') ||
      trigger.closest('[aria-haspopup]') ||
      trigger.parentElement;

    // Use the control (or trigger) as the target for humanLikeClick
    // humanLikeClick will find what's actually at the center coordinates
    const clickTarget = (control as HTMLElement) || trigger;

    // Use humanLikeClick - this will dispatch events to whatever element
    // is actually at the center of the control (could be dropdown indicator,
    // placeholder text, etc.) which is more reliable for React-Select
    const actualClickedElement = this.humanLikeClick(clickTarget, {
      focus: true,  // Focus will be applied to the actual clicked element
      includePointerEvents: true,
      nativeClickFallback: false
    });

    // Send focus event on trigger (important for React/Vue components)
    trigger.dispatchEvent(new FocusEvent('focus', { bubbles: true }));

    // For inputs, also send input event to trigger any watchers
    if (trigger instanceof HTMLInputElement) {
      trigger.dispatchEvent(new Event('input', { bubbles: true }));
    }

    this.logger.log('[ActionExecutor] ✅ Combobox/dropdown humanLikeClick complete, actual element:', 
      actualClickedElement.tagName);
  }

  /**
   * Public method to open a dropdown (for use by OnboardingSDK)
   */
  public openDropdown(trigger: HTMLElement): void {
    this.handleComboboxClick(trigger);
  }

  /**
   * Public method to trigger React onClick (for use by OnboardingSDK)
   */
  public triggerReactOnClickPublic(element: HTMLElement): boolean {
    return this.triggerReactOnClick(element);
  }


  /**
   * Find a dropdown option by text in any open menu/listbox
   * Works with various dropdown libraries
   */
  /**
   * Find a dropdown option by text in any open menu/listbox.
   * Delegates to shared dropdown-helpers module.
   */
  private findDropdownOption(text: string): HTMLElement | null {
    return _findVisibleDropdownOption(text, HybridTargetResolver.isVisible);
  }

  /**
   * Check if element appears to be a dropdown option
   */
  private isDropdownOption(element: HTMLElement): boolean {
    // Check for ARIA role
    if (element.getAttribute('role') === 'option') {
      return true;
    }

    // Check for common class patterns - handle SVG elements
    const className = typeof element.className === 'string' ? element.className : '';
    if (className.includes('option') || className.includes('item')) {
      // Verify it's inside a menu/listbox
      const container = element.closest('[role="listbox"], [role="menu"], [class*="menu"], [class*="listbox"]');
      return container !== null;
    }

    return false;
  }

  /**
   * Perform a complete click action on an element
   * Used for dropdown options and other simple click targets
   */
  /**
   * Perform a complete click action on an element
   * Used for dropdown options and other simple click targets
   * 
   * Uses humanLikeClick for more reliable clicking that dispatches to
   * whatever element is actually at the center coordinates.
   */
  private performClick(element: HTMLElement): void {
    // nativeClickFallback is FALSE, matching every other call site
    // (executeOnElement, the batch executor, PageTracker's replay).
    //
    // It used to be true "for dropdown options", on the theory that libraries like
    // React-Select need synthetic AND native. But humanLikeClick's synthetic
    // sequence already ends in a bubbling `click`, so the fallback delivers a
    // SECOND one to the same handler. On a framework that listens for `click` —
    // Angular, which is what LeadRat is — one bot click ran the host handler twice:
    // two modals from one opener, or a multi-select toggled on and straight back
    // off, which reads as "the bot clicked and nothing happened".
    //
    // This is the same failure family types.ts:300-305 already documents for
    // Angular CDK ("duplicate modal instantiation"); the event ORDER was fixed
    // there, the duplicate click two lines below it was not.
    //
    // If a widget ever genuinely needs a native click, make it conditional on the
    // synthetic one having done nothing — never unconditional.
    this.humanLikeClick(element, {
      focus: true,
      includePointerEvents: true,
      nativeClickFallback: false
    });
  }

  /**
   * Check if element (or its parents) has a React onClick handler
   * Must check same properties as triggerReactOnClick for consistency
   */
  private hasReactOnClickHandler(element: HTMLElement): boolean {
    const reactPropPrefixes = ['__reactProps', '__reactFiber', '__reactEventHandlers'];

    // Check element itself
    try {
      const keys = Object.keys(element);
      for (const key of keys) {
        if (reactPropPrefixes.some(prefix => key.startsWith(prefix))) {
          const reactData = (element as any)[key];
          if (reactData?.onClick) {
            return true;
          }
        }
      }
    } catch (e) {
      // SVG elements or other special elements might throw
    }

    // Check parent elements (React often delegates to parent)
    // Use same depth as triggerReactOnClick (5) for consistency
    let parent = element.parentElement;
    let depth = 0;
    while (parent && depth < 5) {
      try {
        const parentKeys = Object.keys(parent);
        for (const key of parentKeys) {
          if (reactPropPrefixes.some(prefix => key.startsWith(prefix))) {
            const reactData = (parent as any)[key];
            if (reactData?.onClick) {
              return true;
            }
          }
        }
      } catch (e) {
        // Continue on error
      }
      parent = parent.parentElement;
      depth++;
    }

    return false;
  }

  /**
   * Find the best clickable element - if element is non-interactive (span/label),
   * look for a parent with cursor-pointer or click handler
   */
  private findClickableElement(element: HTMLElement): HTMLElement {
    // An associated label is the browser's activation target for its control.
    // Climbing to a styled wrapper can discard that native activation behavior.
    if (element instanceof HTMLLabelElement && element.control) return element;
    const nonInteractiveTags = ['SPAN', 'LABEL', 'P', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'TEXT', 'SVG', 'PATH'];

    // Safely get className (SVG elements have SVGAnimatedString)
    const getClassString = (el: HTMLElement): string => {
      if (!el.className) return '';
      if (typeof el.className === 'string') return el.className;
      // SVGAnimatedString has baseVal property
      if (typeof (el.className as any).baseVal === 'string') return (el.className as any).baseVal;
      return '';
    };

    this.logger.log('[ActionExecutor] Finding clickable element for:', element.tagName, getClassString(element).substring(0, 50));

    // If element is already interactive, use it
    if (!nonInteractiveTags.includes(element.tagName)) {
      this.logger.log('[ActionExecutor] Element is already interactive');
      return element;
    }

    // Look for parent with cursor-pointer class or onclick handler
    let current: HTMLElement | null = element.parentElement;
    let bestCandidate = element;
    let depth = 0;

    while (current && current !== document.body && depth < 10) {
      depth++;
      const style = window.getComputedStyle(current);
      const hasCursorPointer = style.cursor === 'pointer';

      // Check for cursor-pointer in class (safely handle SVG elements)
      const classStr = getClassString(current);
      const hasCursorClass = classStr.includes('cursor-pointer');
      const hasListClass = current.classList?.contains('cursor-pointer');

      const hasOnClick = current.hasAttribute('onclick') ||
        current.hasAttribute('data-onclick') ||
        current.getAttribute('role') === 'button' ||
        current.getAttribute('role') === 'menuitem';
      const isButton = current.tagName === 'BUTTON' || current.tagName === 'A';

      this.logger.log(`[ActionExecutor] Checking parent #${depth}:`, current.tagName,
        'cursor:', style.cursor,
        'hasCursorClass:', hasCursorClass || hasListClass,
        'classes:', classStr.substring(0, 60));

      // Check if this element is clickable
      const isClickable = hasCursorPointer || hasCursorClass || hasListClass || hasOnClick || isButton;

      if (isClickable) {
        bestCandidate = current;
        this.logger.log('[ActionExecutor] ✅ Found clickable parent:', current.tagName,
          classStr.substring(0, 80));
        // Stop at buttons or explicit role=button
        if (isButton || current.getAttribute('role') === 'button') {
          break;
        }
      }

      current = current.parentElement;
    }

    this.logger.log('[ActionExecutor] Final click target:', bestCandidate.tagName,
      getClassString(bestCandidate).substring(0, 60));
    return bestCandidate;
  }

  /**
   * Try to trigger React's internal onClick handler directly
   * React stores handlers in internal fiber properties
   * Returns true if onClick was found and called
   */
  private triggerReactOnClick(element: HTMLElement): boolean {
    try {
      // React 16/17 stores handlers in __reactEventHandlers or __reactProps
      // React 18 uses different internal properties
      const keys = Object.keys(element);

      for (const key of keys) {
        if (key.startsWith('__reactFiber') || key.startsWith('__reactProps') ||
          key.startsWith('__reactEventHandlers')) {
          const reactData = (element as any)[key];
          this.logger.log('[ActionExecutor] Found React property:', key);

          if (reactData?.onClick) {
            this.logger.log('[ActionExecutor] 🎯 Calling React onClick directly!');
            // Create a synthetic-like event object with all methods React might use
            const nativeEvent = new MouseEvent('click', { bubbles: true, cancelable: true });
            const syntheticEvent = {
              type: 'click',
              target: element,
              currentTarget: element,
              nativeEvent: nativeEvent,
              bubbles: true,
              cancelable: true,
              defaultPrevented: false,
              eventPhase: 0,
              isTrusted: false,
              timeStamp: Date.now(),
              // Methods React might call
              preventDefault: () => { (syntheticEvent as any).defaultPrevented = true; },
              stopPropagation: () => { },
              persist: () => { }, // React synthetic events have this
              isDefaultPrevented: () => (syntheticEvent as any).defaultPrevented,
              isPropagationStopped: () => false,
            };
            reactData.onClick(syntheticEvent);
            return true;
          }
        }
      }

      // Also check parent elements for delegated handlers
      // Must check same prefixes as hasReactOnClickHandler for consistency
      const reactPropPrefixes = ['__reactProps', '__reactFiber', '__reactEventHandlers'];
      let parent = element.parentElement;
      let depth = 0;
      while (parent && depth < 5) {
        const parentKeys = Object.keys(parent);
        for (const key of parentKeys) {
          if (reactPropPrefixes.some(prefix => key.startsWith(prefix))) {
            const reactData = (parent as any)[key];
            if (reactData?.onClick) {
              this.logger.log('[ActionExecutor] 🎯 Found onClick on parent, calling directly!');
              // Create a synthetic-like event object with all methods React might use
              const nativeEvent = new MouseEvent('click', { bubbles: true, cancelable: true });
              const syntheticEvent = {
                type: 'click',
                target: element,
                currentTarget: parent,
                nativeEvent: nativeEvent,
                bubbles: true,
                cancelable: true,
                defaultPrevented: false,
                eventPhase: 0,
                isTrusted: false,
                timeStamp: Date.now(),
                // Methods React might call
                preventDefault: () => { (syntheticEvent as any).defaultPrevented = true; },
                stopPropagation: () => { },
                persist: () => { }, // React synthetic events have this
                isDefaultPrevented: () => (syntheticEvent as any).defaultPrevented,
                isPropagationStopped: () => false,
              };
              reactData.onClick(syntheticEvent);
              return true;
            }
          }
        }
        parent = parent.parentElement;
        depth++;
      }

      this.logger.log('[ActionExecutor] No React onClick handler found');
      return false;
    } catch (err) {
      this.logger.log('[ActionExecutor] Error accessing React internals:', err);
      return false;
    }
  }

  /**
   * Manually send click metadata for actions that don't trigger DOM events
   * (like direct React onClick calls)
   */
  private sendClickMetadata(element: HTMLElement, originalElement: HTMLElement): void {
    // Safely get className (SVG elements have SVGAnimatedString)
    const getClassString = (el: HTMLElement): string => {
      if (!el.className) return '';
      if (typeof el.className === 'string') return el.className;
      if (typeof (el.className as any).baseVal === 'string') return (el.className as any).baseVal;
      return '';
    };

    // Build selector path
    const path: string[] = [];
    let current: HTMLElement | null = element;
    while (current && current !== document.body && path.length < 5) {
      let selector = current.tagName.toLowerCase();
      if (current.id) {
        selector = `#${current.id}`;
      } else {
        const classStr = getClassString(current);
        if (classStr) {
          const classes = classStr.trim().split(/\s+/).slice(0, 3).join('.');
          if (classes) selector += '.' + classes;
        }
      }
      path.unshift(selector);
      current = current.parentElement;
    }

    // Get only direct text content, not all descendants
    // This avoids huge text strings for container elements
    const getDirectText = (el: HTMLElement): string => {
      // First, try to find a span/label child with text (common pattern)
      const textChild = el.querySelector('span, label, p');
      if (textChild?.textContent?.trim()) {
        return textChild.textContent.trim().substring(0, 50);
      }

      // Otherwise, get only immediate text nodes
      let directText = '';
      for (const node of Array.from(el.childNodes)) {
        if (node.nodeType === Node.TEXT_NODE) {
          directText += (node.textContent || '').trim() + ' ';
        }
      }
      directText = directText.trim();

      // If no direct text, fall back to textContent but limit it
      if (!directText && el.textContent) {
        directText = el.textContent.trim().substring(0, 50);
      }

      return directText;
    };

    // Determine event source: bot action vs user action
    // Mirrors PageTracker's bot/user tagging for DOM events
    const isBot = this.sdkHost?.isBotActive() === true;

    // `type` is a literal, not caller-controlled: this method has exactly one
    // shape of payload to report, and ReactClickMetadata pins it.
    const clickData: ReactClickMetadata = {
      type: 'click',
      selector: path.join(' > '),
      // Use originalElement for text (the actual target) since element may be a parent
      text: getDirectText(originalElement),
      tagName: element.tagName.toLowerCase(),
      timestamp: Date.now(),
      source: isBot ? 'bot' : 'user',
      sourceDetail: 'react_onclick'  // Preserve React-specific diagnostic info
    };

    this.logger.log('[ActionExecutor] 📤 Sending React onClick metadata:', clickData.text, `(source: ${clickData.source})`);

    if (this.sdkHost) {
      this.sdkHost.sendClickMetadata(clickData);
    } else {
      this.logger.warn('[ActionExecutor] ⚠️ No SDK host wired, cannot send metadata');
    }
  }


  /**
   * Check if action is destructive
   */
  private isDestructive(action: UIAction): boolean {
    const destructiveTypes = ['submit', 'delete', 'remove', 'clear'];
    const destructiveSelectors = ['[type="submit"]', '.delete', '.remove'];

    return destructiveTypes.some(type => action.description?.toLowerCase().includes(type)) ||
      destructiveSelectors.some(sel => action.selector.includes(sel));
  }

  /**
   * Check if hybrid action is destructive
   */
  private isHybridActionDestructive(action: HybridUIAction): boolean {
    const destructiveKeywords = ['submit', 'delete', 'remove', 'clear', 'confirm', 'proceed'];
    const description = action.description?.toLowerCase() || '';
    const targetText = action.target.text?.toLowerCase() || '';

    return destructiveKeywords.some(keyword =>
      description.includes(keyword) || targetText.includes(keyword)
    );
  }

  /**
   * Get user consent for legacy action
   */
  private async getConsent(action: UIAction): Promise<boolean> {
    const message = `Allow action: ${action.description || action.type} on ${action.selector}?`;
    return confirm(message);
  }

  /**
   * Get user consent for hybrid action
   */
  private async getHybridConsent(action: HybridUIAction): Promise<boolean> {
    const targetDesc = action.target.text || action.target.selector || 'element';
    const message = `Allow "${action.action}" on "${targetDesc}"?\n${action.description || ''}`;
    return confirm(message);
  }

  /**
   * Execute the legacy action
   */
  private async execute(action: UIAction): Promise<boolean> {
    // SPECIAL HANDLING: Virtual selectors with data-option-text or data-label
    // These attributes don't exist in DOM, they're hints for the SDK
    if (action.type === 'click') {
      // Check for dropdown option selector
      const optionText = this.parseOptionTextFromSelector(action.selector);
      if (optionText) {
        this.logger.log('[ActionExecutor] Virtual selector detected - finding option by text:', optionText);
        const option = this.findDropdownOption(optionText);
        if (option) {
          this.performClick(option);
          return true;
        }
        this.logger.warn('[ActionExecutor] Option not found by text:', optionText);
        return false;
      }

      // Check for labeled combobox selector
      const labelText = this.parseLabelFromSelector(action.selector);
      if (labelText) {
        this.logger.log('[ActionExecutor] Virtual selector detected - finding combobox by label:', labelText);
        const combobox = this.findComboboxByLabel(labelText);
        if (combobox) {
          this.handleComboboxClick(combobox);
          return true;
        }
        this.logger.warn('[ActionExecutor] Combobox not found by label:', labelText);
        return false;
      }
    }

    // Strip virtual attributes from selector for querySelector
    const cleanSelector = this.stripVirtualAttributes(action.selector);
    const element = document.querySelector(cleanSelector) as HTMLElement;
    if (!element) {
      this.logger.warn('[ActionExecutor] Element not found:', cleanSelector);
      return false;
    }

    switch (action.type) {
      case 'click':
        // Use simulateClick for proper dropdown/React handling
        this.simulateClick(element, action.selector);
        break;

      case 'type':
        if (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement) {
          element.focus();
          element.value = action.value || '';
          element.dispatchEvent(new Event('input', { bubbles: true }));
          element.dispatchEvent(new Event('change', { bubbles: true }));
        }
        break;

      case 'select':
        // Use dropdown handling for select actions
        const combobox = this.findComboboxInput(element);
        if (combobox) {
          // Open the dropdown first
          this.handleComboboxClick(combobox);
          // Wait for dropdown to open, then select value
          await new Promise(resolve => setTimeout(resolve, 200));
          if (action.value) {
            const option = this.findDropdownOption(action.value);
            if (option) {
              this.performClick(option);
            }
          }
        } else if (element instanceof HTMLSelectElement) {
          element.value = action.value || '';
          element.dispatchEvent(new Event('change', { bubbles: true }));
        }
        break;

      case 'hover':
        element.dispatchEvent(new MouseEvent('mouseenter', { bubbles: true }));
        element.dispatchEvent(new MouseEvent('mouseover', { bubbles: true }));
        break;

      case 'scroll':
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        break;

      default:
        this.logger.warn('[ActionExecutor] Unknown action type:', action.type);
        return false;
    }

    return true;
  }
}
